import { pgTable, text, varchar, integer, bigint, boolean, timestamp, json, jsonb, date, doublePrecision, numeric, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { isValidOrgNumber } from "./invoice-recipient";
import { checkShippingAddressLength } from "./shipping-address";

// Organisasjonsnummer paa fakturamottakeren: valgfritt, men ni siffer naar det
// foerst er fylt ut (mellomrom tillatt -- «925 137 529»).
const optionalOrgNumberSchema = z
  .string()
  .nullable()
  .optional()
  .refine(
    (value) => !value || !value.trim() || isValidOrgNumber(value),
    { message: "Organisasjonsnummer må ha ni siffer" },
  );

// User roles with hierarchy (AAAAA = highest, A = lowest)
export const USER_ROLES = {
  superadmin: { level: 5, label: "Superadmin", code: "AAAAA" },
  admin: { level: 4, label: "Admin", code: "AAAA" },
  tech_crew: { level: 3, label: "Teknisk crew", code: "AAA" },
  warehouse: { level: 2, label: "Lager", code: "AA" },
  driver: { level: 1, label: "Sjåfør", code: "A" },
} as const;

export type UserRole = keyof typeof USER_ROLES;
export const userRoleValues = Object.keys(USER_ROLES) as UserRole[];

// Helper to get role level
export function getRoleLevel(role: string): number {
  const roleInfo = USER_ROLES[role as UserRole];
  return roleInfo ? roleInfo.level : 0;
}

// Get roles that a user with given role can see (equal or lower level)
export function getVisibleRoles(userRole: string): UserRole[] {
  const userLevel = getRoleLevel(userRole);
  return userRoleValues.filter(role => getRoleLevel(role) <= userLevel);
}

// Check if user can view/manage another user based on role hierarchy
export function canManageRole(managerRole: string, targetRole: string): boolean {
  return getRoleLevel(managerRole) > getRoleLevel(targetRole);
}

// Check if user can view another user (same or lower level)
export function canViewRole(viewerRole: string, targetRole: string): boolean {
  return getRoleLevel(viewerRole) >= getRoleLevel(targetRole);
}

// Check if user is superadmin (can manage all users including admins)
export function isSuperAdmin(role: string): boolean {
  return role === "superadmin";
}

// Check if user can promote/demote to admin role (only superadmins can)
export function canManageAdminRole(managerRole: string): boolean {
  return getRoleLevel(managerRole) > getRoleLevel("admin");
}

// Users table for auth
export const users = pgTable("users", {
  id: varchar("id", { length: 36 }).primaryKey(),
  // UNIK på lower(username) via runtime-indeksen users_username_lower_uq
  // (runMigrations i server/db.ts) — login-oppslaget er case-insensitivt.
  username: text("username").notNull(),
  password: text("password").notNull(),
  role: text("role").notNull().default("warehouse"),
  email: text("email"),
  phone: text("phone"),
  company: text("company"),
  active: boolean("active").default(true),
  allWarehouses: boolean("all_warehouses").default(false), // If true, user has access to all warehouses (for warehouse role)
  signatureRole: text("signature_role"), // Role for contract signing, e.g. "Bookingagent", "Daglig leder", "Markedsansvarlig"
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
  role: true,
  email: true,
  phone: true,
  company: true,
  allWarehouses: true,
  signatureRole: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

// User activity tracking
export type ActivityType = "washed" | "washed_not_ordered" | "charged" | "packed" | "sent" | "received" | "inspected";

export const userActivities = pgTable("user_activities", {
  id: varchar("id", { length: 36 }).primaryKey(),
  userId: varchar("user_id", { length: 36 }).notNull(),
  activityType: text("activity_type").notNull(),
  quantity: integer("quantity").default(1),
  orderId: varchar("order_id", { length: 36 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUserActivitySchema = createInsertSchema(userActivities).omit({
  id: true,
  createdAt: true,
});

export type InsertUserActivity = z.infer<typeof insertUserActivitySchema>;
export type UserActivity = typeof userActivities.$inferSelect;

// User-Warehouse assignment (for warehouse workers linked to specific warehouses)
export const userWarehouses = pgTable("user_warehouses", {
  id: varchar("id", { length: 36 }).primaryKey(),
  userId: varchar("user_id", { length: 36 }).notNull(),
  warehouseId: varchar("warehouse_id", { length: 36 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUserWarehouseSchema = createInsertSchema(userWarehouses).omit({
  id: true,
  createdAt: true,
});

export type InsertUserWarehouse = z.infer<typeof insertUserWarehouseSchema>;
export type UserWarehouse = typeof userWarehouses.$inferSelect;

// Order statuses
export type OrderStatus =
  | "draft"
  | "created"
  | "packed"
  | "sent"
  | "delivered"
  | "received"
  | "return_initiated"
  | "return_sent"
  | "return_received"
  | "inspected"
  | "completed"
  // Terminal sidegren: kansellert foer utstyret forlot lageret. Beholder
  // historikken, i motsetning til DELETE.
  | "cancelled";

// Product types
export type ProductType = "headset" | "transmitter" | "charger_16" | "adapter_lightning" | "adapter_usbc";

// Delivery methods
export type DeliveryMethod = "pickup" | "posten" | "bring" | "other";

// Orders table
export const orders = pgTable("orders", {
  id: varchar("id", { length: 36 }).primaryKey(),
  // UNIK via runtime-indeksen orders_order_number_uq (runMigrations i server/db.ts).
  orderNumber: text("order_number").notNull(),
  status: text("status").notNull().default("created"),
  
  // Customer info
  customerName: text("customer_name").notNull(),
  customerCompany: text("customer_company"),
  customerEmail: text("customer_email").notNull(),
  customerPhone: text("customer_phone"),
  customerAddress: text("customer_address"),
  customerPostalCode: varchar("customer_postal_code", { length: 10 }),
  customerCity: text("customer_city"),

  // Order-specific delivery address override (different shipping address for THIS order)
  hasCustomDelivery: boolean("has_custom_delivery").default(false),
  deliveryName: text("delivery_name"),
  deliveryAddress: text("delivery_address"),
  deliveryPostalCode: varchar("delivery_postal_code", { length: 10 }),
  deliveryCity: text("delivery_city"),
  deliveryPhone: text("delivery_phone"),

  // Order-specific invoice information (references, PO numbers for THIS order)
  hasCustomInvoice: boolean("has_custom_invoice").default(false),
  invoiceDepartment: text("invoice_department"),
  invoiceReference: text("invoice_reference"),
  invoiceContactPerson: text("invoice_contact_person"),
  invoicePhone: text("invoice_phone"),
  invoiceEmail: text("invoice_email"),
  invoicePurchaseOrder: text("invoice_purchase_order"),
  invoicePoNumber: text("invoice_po_number"),
  // Fakturamottaker som avviker fra avtaleparten (sjelden): fakturaen stilles
  // til et annet selskap, mens den som signerer fortsatt er avtalepart og
  // ansvarlig for betaling. Begge feltene settes sammen -- se
  // @shared/invoice-recipient. Ingen adressefelt: Fiken-kontakten identifiseres
  // paa organisasjonsnummer, og ordrens adresse tilhoerer avtaleparten.
  invoiceRecipientCompany: text("invoice_recipient_company"),
  invoiceRecipientOrgNumber: text("invoice_recipient_org_number"),

  // Avtalt betalingsfrist (dager fra fakturadato), arvet fra tilbudet ved
  // bekreftelse. Fiken og fakturakoeen leser denne foer kundekortets frist.
  // Se @shared/payment-terms.
  paymentTermsDays: integer("payment_terms_days"),

  // Avtalt kurér-/budbilpris, arvet fra tilbudet ved bekreftelse.
  manualShippingPrice: numeric("manual_shipping_price", { precision: 10, scale: 2 }),

  // Kontrahenten: personen som signerer paa vegne av kunden. Ligger paa ordren
  // fordi samme organisasjon kan ha ulike ansvarlige per arrangement.
  contactPersonName: text("contact_person_name"),
  contactPersonEmail: text("contact_person_email"),
  contactPersonPhone: text("contact_person_phone"),

  // Options
  customerWashesEquipment: boolean("customer_washes_equipment").default(false),
  customerChargesEquipment: boolean("customer_charges_equipment").default(false),
  
  // Washing service
  washingPreOrdered: boolean("washing_pre_ordered").default(false),
  washingQuantity: integer("washing_quantity").default(0),
  washingCostPerUnit: numeric("washing_cost_per_unit", { precision: 10, scale: 2 }).default("5.00"),
  
  // Charging service
  chargingPreOrdered: boolean("charging_pre_ordered").default(false),
  chargingQuantity: integer("charging_quantity").default(0),
  
  // Event planning fields
  eventName: text("event_name"),
  eventDate: date("event_date"),
  latestArrivalDate: date("latest_arrival_date"),
  sendDate: date("send_date"),
  // Dates for extra rental days (Dag 2, Dag 3, ...). Map of dayNumber -> ISO date.
  // Day 1 always uses eventDate; only Day 2+ are stored here. Missing entries
  // default to eventDate + (dayNumber - 1) days.
  dayDates: json("day_dates").$type<Record<string, string>>(),
  // Avtalt returfrist: overstyrer den beregnede fristen (foerste virkedag etter
  // siste arrangementsdag) naar kunden har avtalt aa beholde utstyret lenger
  // (f.eks. retur 27. august, lenge etter arrangementet). Tom = dagens regel.
  returnDeadlineOverride: date("return_deadline_override"),
  numberOfPackages: integer("number_of_packages").default(1),
  packingPlan: json("packing_plan"),
  packingInstructions: text("packing_instructions"),
  
  // Delivery
  deliveryMethod: text("delivery_method").notNull().default("posten"),
  trackingNumber: text("tracking_number"),
  packageTrackingCodes: json("package_tracking_codes"), // Array of tracking codes, one per colli
  deliveryNotes: text("delivery_notes"),
  
  // Shipping selections (references to shipping options)
  outboundShippingId: varchar("outbound_shipping_id", { length: 36 }),
  returnShippingId: varchar("return_shipping_id", { length: 36 }),
  // Vektklasse for fraktetiketten ("10" | "25" | "35" kg). Tom = automatisk:
  // posten-manager utleder klassen av headset per kolli. Feltet er en
  // OVERSTYRING, ikke en fasit -- posten-manager HEVER fortsatt til 35 kg naar
  // det er over 72 headset per kolli, saa en manuell "25" paa en tung ordre
  // blir 35 likevel. Settes fra fraktbookingkoeen
  // (PATCH /api/orders/:id/shipping-override).
  weightClassOverride: varchar("weight_class_override", { length: 2 }),

  // Return
  returnCode: text("return_code"),
  returnTrackingNumber: text("return_tracking_number"),
  returnPackageTrackingCodes: json("return_package_tracking_codes"), // Array of return tracking codes, one per colli
  hasReceivingDiscrepancy: boolean("has_receiving_discrepancy").default(false),
  
  // Customer portal access
  accessCode: text("access_code"),
  accessCodeExpiresAt: timestamp("access_code_expires_at"),
  accessCodeRevokedAt: timestamp("access_code_revoked_at"),

  // Internt notat (ALDRI vist til kunde, se buildPublicOrderView-allowlisten
  // og customer-portal-endepunktene). Retur-notater fra mottaksdialogen
  // appendes hit i tillegg til å logges i auditLogs.
  notes: text("notes"),

  // Beskjed til dem som tar imot og kontrollerer returen. Egen kolonne, ikke
  // en del av notes: notes vokser med hver mottaksregistrering og blir lang,
  // og en viktig beskjed drukner da. Denne er kort, kuratert av admin, og
  // vises hoeyt i mottaks- og kontrollkoeen.
  //
  // Bakgrunnen er saker der utstyr allerede er fakturert som manglende og en
  // ny retur er paa vei: uten beskjeden ville lageret registrert avvik paa
  // nytt paa noe som er gjort opp. ALDRI vist til kunde, samme regel som notes.
  returnNote: text("return_note"),
  returnNoteUpdatedAt: timestamp("return_note_updated_at"),
  returnNoteUpdatedByName: text("return_note_updated_by_name"),

  // Timestamps
  createdAt: timestamp("created_at").defaultNow(),
  packedAt: timestamp("packed_at"),
  sentAt: timestamp("sent_at"),
  receivedAt: timestamp("received_at"),
  returnInitiatedAt: timestamp("return_initiated_at"),
  returnReceivedAt: timestamp("return_received_at"),
  completedAt: timestamp("completed_at"),
  // Faktisk henting er et eget ordrefaktum. Henteordrer beholder status
  // "sent" frem til retur, saa status alene kan ikke skille klar fra hentet.
  pickedUpAt: timestamp("picked_up_at"),
  pickedUpBy: varchar("picked_up_by", { length: 36 }),
  pickedUpByName: text("picked_up_by_name"),
  // Fakturering (manuelt satt av admin) - uavhengig av fulfillment-status
  invoicedAt: timestamp("invoiced_at"),
  paidAt: timestamp("paid_at"),
  // Kunden betaler med Vipps-betalingslenke (manuelt laget i Vipps-systemet).
  // Flagget gjor kravet synlig og gater klar-for-henting til lenken er satt.
  paymentViaVippsLink: boolean("payment_via_vipps_link").default(false),
  vippsPaymentLink: text("vipps_payment_link"),

  // Assigned user
  assignedUserId: varchar("assigned_user_id", { length: 36 }),
  
  // Warehouse
  warehouseId: varchar("warehouse_id", { length: 36 }),
  
  // Pricing (NOK with 2 decimal places)
  rentalListTotal: numeric("rental_list_total", { precision: 10, scale: 2 }).default("0.00"), // Veiledende pris for leieobjekter
  rentalAgreedTotal: numeric("rental_agreed_total", { precision: 10, scale: 2 }).default("0.00"), // Avtalt pris for leieobjekter
  servicesListTotal: numeric("services_list_total", { precision: 10, scale: 2 }).default("0.00"), // Veiledende pris for tjenester
  servicesAgreedTotal: numeric("services_agreed_total", { precision: 10, scale: 2 }).default("0.00"), // Avtalt pris for tjenester
  totalListPrice: numeric("total_list_price", { precision: 10, scale: 2 }).default("0.00"), // Total veiledende pris
  totalAgreedPrice: numeric("total_agreed_price", { precision: 10, scale: 2 }).default("0.00"), // Total avtalt pris
  discountAmount: numeric("discount_amount", { precision: 10, scale: 2 }).default("0.00"), // Rabatt (listpris - avtalt pris)
  vatExempt: boolean("vat_exempt").default(false), // MVA-fritak
  vatAmount: numeric("vat_amount", { precision: 10, scale: 2 }).default("0.00"), // MVA (25%)
  grandTotal: numeric("grand_total", { precision: 10, scale: 2 }).default("0.00"), // Totalpris inkl. MVA
  currency: text("currency").default("NOK"),
  
  // Signed contract file
  signedContractPath: text("signed_contract_path"),
  // Admin-overstyring: tillat fraktbooking foer kontrakten er signert
  // (legacy-/hasteordrer der kunden har bekreftet men ikke rukket aa signere).
  shippingContractOverride: boolean("shipping_contract_override").default(false),
  signedContractUploadedAt: timestamp("signed_contract_uploaded_at"),
});

export const insertOrderSchema = createInsertSchema(orders).omit({
  id: true,
  orderNumber: true,
  createdAt: true,
  packedAt: true,
  sentAt: true,
  receivedAt: true,
  returnInitiatedAt: true,
  returnReceivedAt: true,
  completedAt: true,
  invoicedAt: true,
  paidAt: true,
});

export type InsertOrder = z.infer<typeof insertOrderSchema>;
export type Order = typeof orders.$inferSelect;

// Order item categories
export type OrderItemCategory = "rental" | "service";

// Order items with packing/receiving tracking
export const orderItems = pgTable("order_items", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  category: text("category").notNull().default("rental"), // rental = leieobjekt, service = tjeneste
  productType: text("product_type").notNull(),
  productName: text("product_name").notNull(),
  quantity: integer("quantity").notNull().default(1),
  dayNumber: integer("day_number").notNull().default(1), // Which day this line represents (Dag 1, Dag 2, etc). dayNumber>1 lines excluded from kolli/vask calculations
  
  // Pricing (NOK with 2 decimal places)
  listPrice: numeric("list_price", { precision: 10, scale: 2 }).default("0.00"), // Veiledende pris per enhet
  agreedPrice: numeric("agreed_price", { precision: 10, scale: 2 }).default("0.00"), // Avtalt pris per enhet
  discountPercent: integer("discount_percent").default(0), // Rabattprosent
  totalListPrice: numeric("total_list_price", { precision: 10, scale: 2 }).default("0.00"), // listPrice * quantity
  totalAgreedPrice: numeric("total_agreed_price", { precision: 10, scale: 2 }).default("0.00"), // agreedPrice * quantity
  
  // Packing/receiving tracking
  packedQuantity: integer("packed_quantity").default(0),
  receivedQuantity: integer("received_quantity").default(0),
  returnedQuantity: integer("returned_quantity").default(0),
  inspectedQuantity: integer("inspected_quantity").default(0),
  condition: text("condition"),
  // Comment/remark field for the item
  comment: text("comment"),

  // Hourly (timespris) services - mirrors quote_items so hourly billing carries
  // over when a quote becomes an order. Billable time rounds up to a started half-hour.
  isHourly: boolean("is_hourly").default(false),
  hourlyRate: numeric("hourly_rate", { precision: 10, scale: 2 }), // Timesats (kr/time)
  setupMinutes: integer("setup_minutes").default(0), // Tid for oppmøte (minutter)
  workMinutes: integer("work_minutes").default(0), // Aktuelt arbeid (minutter)
  returnMinutes: integer("return_minutes").default(0), // Tid for retur (minutter)
  adminMinutes: integer("admin_minutes").default(0), // Fast administrasjons-halvtime (minutter)
  billableMinutes: integer("billable_minutes").default(0), // Sum avrundet opp til påbegynt halvtime + admin
  estimatedHours: numeric("estimated_hours", { precision: 10, scale: 2 }), // Estimert antall timer (for fremtidig betalingsintegrasjon)
});

export const insertOrderItemSchema = createInsertSchema(orderItems).omit({
  id: true,
});

export type InsertOrderItem = z.infer<typeof insertOrderItemSchema>;
export type OrderItem = typeof orderItems.$inferSelect;

// Order packages (colli) - tracks packing and shipping status per package
export const orderPackages = pgTable("order_packages", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  packageIndex: integer("package_index").notNull(), // 1-based index
  label: text("label"), // Optional label like "Kolli 1"
  status: text("status").notNull().default("pending"), // pending, packed, sent
  packedAt: timestamp("packed_at"),
  packedBy: varchar("packed_by", { length: 36 }),
  packedByName: text("packed_by_name"),
  sentAt: timestamp("sent_at"),
  sentBy: varchar("sent_by", { length: 36 }),
  sentByName: text("sent_by_name"),
  trackingNumber: text("tracking_number"),
});

export const insertOrderPackageSchema = createInsertSchema(orderPackages).omit({
  id: true,
  packedAt: true,
});

export type InsertOrderPackage = z.infer<typeof insertOrderPackageSchema>;
export type OrderPackage = typeof orderPackages.$inferSelect;

// Order package items - what items go in each package
export const orderPackageItems = pgTable("order_package_items", {
  id: varchar("id", { length: 36 }).primaryKey(),
  packageId: varchar("package_id", { length: 36 }).notNull(),
  orderItemId: varchar("order_item_id", { length: 36 }).notNull(),
  productType: text("product_type").notNull(),
  productName: text("product_name").notNull(),
  plannedQuantity: integer("planned_quantity").notNull().default(0),
  packedQuantity: integer("packed_quantity").default(0),
});

export const insertOrderPackageItemSchema = createInsertSchema(orderPackageItems).omit({
  id: true,
});

export type InsertOrderPackageItem = z.infer<typeof insertOrderPackageItemSchema>;
export type OrderPackageItem = typeof orderPackageItems.$inferSelect;

// Order tracking codes - QR codes for packages (outbound and return)
export const orderTrackingCodes = pgTable("order_tracking_codes", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  packageIndex: integer("package_index").notNull(), // 1-based index matching package number
  codeType: text("code_type").notNull(), // "outbound" or "return"
  codeImage: text("code_image"), // Base64 data URL of the QR code image (text for large images)
  codeValue: text("code_value"), // Optional: link URL to sendingskode/returkode
  uploadedAt: timestamp("uploaded_at").defaultNow(),
  uploadedBy: varchar("uploaded_by", { length: 36 }),
});

export const insertOrderTrackingCodeSchema = createInsertSchema(orderTrackingCodes).omit({
  id: true,
  uploadedAt: true,
});

export type InsertOrderTrackingCode = z.infer<typeof insertOrderTrackingCodeSchema>;
export type OrderTrackingCode = typeof orderTrackingCodes.$inferSelect;

// Media/attachments for orders
export const media = pgTable("media", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  type: text("type").notNull(), // e.g., "attachment", "contract_pdf", "inspection_photo"
  url: text("url").notNull(),
  originalName: text("original_name"),
  displayName: text("display_name"),
  mimeType: text("mime_type"),
  size: integer("size"),
  description: text("description"),
  contractId: varchar("contract_id", { length: 36 }),
  uploadedBy: varchar("uploaded_by", { length: 36 }),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
  // Bildebank (avvik): naar bildet ble sendt til kunden med en avviks-e-post.
  // Null = fortsatt i "banken" (ubrukt); satt = markert oversendt, ikke send paa nytt.
  sentToCustomerAt: timestamp("sent_to_customer_at"),
});

export const insertMediaSchema = createInsertSchema(media).omit({
  id: true,
  uploadedAt: true,
  sentToCustomerAt: true,
});

export type InsertMedia = z.infer<typeof insertMediaSchema>;
export type Media = typeof media.$inferSelect;

// Issues/deviations
export const issues = pgTable("issues", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  type: text("type").notNull(),
  description: text("description").notNull(),
  resolved: boolean("resolved").default(false),
  reportedBy: varchar("reported_by", { length: 36 }),
  reportedAt: timestamp("reported_at").defaultNow(),
  resolvedAt: timestamp("resolved_at"),
  resolvedNotes: text("resolved_notes"),
});

export const insertIssueSchema = createInsertSchema(issues).omit({
  id: true,
  reportedAt: true,
  resolvedAt: true,
  resolvedNotes: true,
});

export type InsertIssue = z.infer<typeof insertIssueSchema>;
export type Issue = typeof issues.$inferSelect;

// Return confirmation
export const returnConfirmations = pgTable("return_confirmations", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  headsetsReturned: integer("headsets_returned").notNull(),
  washedConfirmed: boolean("washed_confirmed").default(false),
  chargedConfirmed: boolean("charged_confirmed").default(false),
  notes: text("notes"),
  returnQuantities: jsonb("return_quantities"),
  photoUrls: text("photo_urls"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertReturnConfirmationSchema = createInsertSchema(returnConfirmations).omit({
  id: true,
  createdAt: true,
});

export type InsertReturnConfirmation = z.infer<typeof insertReturnConfirmationSchema>;
export type ReturnConfirmation = typeof returnConfirmations.$inferSelect;

// Warehouse inspection
export const inspections = pgTable("inspections", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  firstCount: integer("first_count"),
  secondCount: integer("second_count"),
  countMismatch: boolean("count_mismatch").default(false),
  washedStatus: boolean("washed_status").default(false),
  chargedStatus: boolean("charged_status").default(false),
  transmittersOk: boolean("transmitters_ok").default(true),
  accessoriesOk: boolean("accessories_ok").default(true),
  notes: text("notes"),
  inspectedBy: varchar("inspected_by", { length: 36 }),
  inspectedAt: timestamp("inspected_at").defaultNow(),
});

export const insertInspectionSchema = createInsertSchema(inspections).omit({
  id: true,
  inspectedAt: true,
});

export type InsertInspection = z.infer<typeof insertInspectionSchema>;
export type Inspection = typeof inspections.$inferSelect;

// Audit log
export const auditLogs = pgTable("audit_logs", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }),
  action: text("action").notNull(),
  description: text("description").notNull(),
  performedBy: varchar("performed_by", { length: 36 }),
  performedByName: text("performed_by_name"),
  metadata: json("metadata"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAuditLogSchema = createInsertSchema(auditLogs).omit({
  id: true,
  createdAt: true,
});

export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type AuditLog = typeof auditLogs.$inferSelect;

// Customers table
export const customers = pgTable("customers", {
  id: varchar("id", { length: 36 }).primaryKey(),
  name: text("name").notNull(),
  isPrivatePerson: boolean("is_private_person").default(false),
  company: text("company"),
  orgNumber: varchar("org_number", { length: 20 }),
  contactRole: text("contact_role"), // Role of contact person in company, e.g. "Daglig leder", "Prosjektleder"
  email: text("email").notNull(),
  phone: text("phone"),
  address: text("address"),
  postalCode: varchar("postal_code", { length: 10 }),
  city: text("city"),
  
  // Delivery address override
  hasCustomDelivery: boolean("has_custom_delivery").default(false),
  deliveryName: text("delivery_name"),
  deliveryAddress: text("delivery_address"),
  deliveryPostalCode: varchar("delivery_postal_code", { length: 10 }),
  deliveryCity: text("delivery_city"),
  deliveryPhone: text("delivery_phone"),
  
  // Invoice information override
  hasCustomInvoice: boolean("has_custom_invoice").default(false),
  invoiceMethod: varchar("invoice_method", { length: 20 }).default("email"), // ehf, email, post
  invoiceEhfReference: text("invoice_ehf_reference"),
  invoiceDepartment: text("invoice_department"),
  invoiceReference: text("invoice_reference"),
  invoiceContactPerson: text("invoice_contact_person"),
  invoicePhone: text("invoice_phone"),
  invoiceEmail: text("invoice_email"),
  invoiceAddress: text("invoice_address"),
  invoicePostalCode: varchar("invoice_postal_code", { length: 10 }),
  invoiceCity: text("invoice_city"),
  // NULL beholder standardregelen i Fiken. Satt verdi overstyrer forfallsdato
  // til fakturadato + dette antallet kalenderdager.
  paymentTermsDays: integer("payment_terms_days"),
  
  birthDate: text("birth_date"),
  washesEquipment: boolean("washes_equipment").default(false),
  chargesEquipment: boolean("charges_equipment").default(false),
  notes: text("notes"),
  archived: boolean("archived").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertCustomerSchema = createInsertSchema(customers).omit({
  id: true,
  createdAt: true,
});

export type InsertCustomer = z.infer<typeof insertCustomerSchema>;
export type Customer = typeof customers.$inferSelect;

// Customer invites for self-registration
export const customerInvites = pgTable("customer_invites", {
  id: varchar("id", { length: 36 }).primaryKey(),
  token: text("token").notNull(),
  email: text("email"),
  status: text("status").notNull().default("pending"),
  customerId: varchar("customer_id", { length: 36 }),
  createdAt: timestamp("created_at").defaultNow(),
  expiresAt: timestamp("expires_at"),
  completedAt: timestamp("completed_at"),
});

export const insertCustomerInviteSchema = createInsertSchema(customerInvites).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});

export type InsertCustomerInvite = z.infer<typeof insertCustomerInviteSchema>;
export type CustomerInvite = typeof customerInvites.$inferSelect;

// Customer registrations (pending approval)
export const customerRegistrations = pgTable("customer_registrations", {
  id: varchar("id", { length: 36 }).primaryKey(),
  token: varchar("token", { length: 64 }).notNull(), // Unique token for registration URL
  status: text("status").notNull().default("pending"), // pending, approved, rejected
  
  // Same fields as customers
  name: text("name").notNull(),
  isPrivatePerson: boolean("is_private_person").default(false),
  company: text("company"),
  orgNumber: varchar("org_number", { length: 20 }),
  contactRole: text("contact_role"),
  email: text("email").notNull(),
  phone: text("phone"),
  address: text("address"),
  postalCode: varchar("postal_code", { length: 10 }),
  city: text("city"),
  
  // Delivery address
  hasCustomDelivery: boolean("has_custom_delivery").default(false),
  deliveryName: text("delivery_name"),
  deliveryAddress: text("delivery_address"),
  deliveryPostalCode: varchar("delivery_postal_code", { length: 10 }),
  deliveryCity: text("delivery_city"),
  deliveryPhone: text("delivery_phone"),
  
  // Invoice information
  hasCustomInvoice: boolean("has_custom_invoice").default(false),
  invoiceMethod: varchar("invoice_method", { length: 20 }).default("email"), // ehf, email, post
  invoiceEhfReference: text("invoice_ehf_reference"),
  invoiceDepartment: text("invoice_department"),
  invoiceReference: text("invoice_reference"),
  invoiceContactPerson: text("invoice_contact_person"),
  invoicePhone: text("invoice_phone"),
  invoiceEmail: text("invoice_email"),
  invoiceAddress: text("invoice_address"),
  invoicePostalCode: varchar("invoice_postal_code", { length: 10 }),
  invoiceCity: text("invoice_city"),
  
  birthDate: text("birth_date"),
  washesEquipment: boolean("washes_equipment").default(false),
  chargesEquipment: boolean("charges_equipment").default(false),
  notes: text("notes"),
  
  // Message to landlord/rental company
  messageToLandlord: text("message_to_landlord"),
  
  // Timestamps
  createdAt: timestamp("created_at").defaultNow(),
  approvedAt: timestamp("approved_at"),
  rejectedAt: timestamp("rejected_at"),
  customerId: varchar("customer_id", { length: 36 }), // Set when approved
  updatedAt: timestamp("updated_at"),
});

export const insertCustomerRegistrationSchema = createInsertSchema(customerRegistrations).omit({
  id: true,
  createdAt: true,
  approvedAt: true,
  rejectedAt: true,
  customerId: true,
  updatedAt: true,
});

export type InsertCustomerRegistration = z.infer<typeof insertCustomerRegistrationSchema>;
export type CustomerRegistration = typeof customerRegistrations.$inferSelect;

// «spam» (11.9.2026): falske henvendelser fra skjemaet. Skal ikke purres paa,
// ikke faa tilbud og ikke telle i treffprosenten. Ren appverdi, ingen DDL.
export const INQUIRY_STATUSES = ["venter", "fulgt_opp", "avtale", "ikke_aktuell", "spam"] as const;
/**
 * Notatlinja serveren skriver naar bookingskjemaet kommer inn fra nettsiden.
 * Klienten leser den for aa se at skjemaet er fylt ut (fanen «Skjema sendt,
 * ikke utfylt»), saa teksten maa vaere den samme paa begge sider.
 */
export const BOOKINGSKJEMA_MOTTATT_MERKE = "Bookingskjema mottatt";
export type InquiryStatus = (typeof INQUIRY_STATUSES)[number];

// Foresporsler fra bookingskjema/manuell registrering foer tilbud eller avtale.
// Status er bevisst en tekstkolonne; appen validerer de fem tillatte verdiene.
export const inquiries = pgTable("inquiries", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  email: text("email"),
  phone: text("phone"),
  location: text("location"),
  headsetCount: integer("headset_count"),
  eventDate: date("event_date"),
  deliveryMethod: text("delivery_method"),
  source: text("source").default("bookingskjema"),
  status: text("status").$type<InquiryStatus>().notNull().default("venter"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  followedUpAt: timestamp("followed_up_at", { withTimezone: true }),
  /**
   * Satt naar kunden har bedt om bookingskjemaet, altsaa sagt «dette vil vi ha».
   * Skiller de varme fra prisforespoerslene, som bare lurer paa hva det koster.
   * Tidspunkt, ikke boolsk: da vet vi ogsaa hvor lenge de har ventet paa avtale.
   */
  bookingFormRequestedAt: timestamp("booking_form_requested_at", { withTimezone: true }),
});

export const insertInquirySchema = createInsertSchema(inquiries).omit({
  id: true,
  createdAt: true,
  followedUpAt: true,
});

export type InsertInquiry = Omit<z.infer<typeof insertInquirySchema>, "status"> & {
  status?: InquiryStatus;
};
export type Inquiry = typeof inquiries.$inferSelect;

/**
 * Hendelseslogg per henvendelse (server/inquiry-events.ts): datagrunnlaget for automatisk
 * standardtilbud. Additiv tabell, opprettes av runMigrations() ved oppstart.
 */
export const inquiryEvents = pgTable("inquiry_events", {
  id: uuid("id").primaryKey().defaultRandom(),
  inquiryId: uuid("inquiry_id").notNull(),
  type: text("type").notNull(),
  at: timestamp("at", { withTimezone: true }).notNull().defaultNow(),
  data: jsonb("data"),
});
export type InquiryEvent = typeof inquiryEvents.$inferSelect;
export interface InsertInquiryEvent {
  inquiryId: string;
  type: string;
  at?: Date;
  data?: Record<string, unknown> | null;
}

// Quote statuses. "awaiting_info": kunden har sagt ja (e-post/telefon) men
// bookingskjemaet er ikke mottatt ennaa - erstatter Excel-kolonnen «venter
// paa info paa avtale». Ren tekstkolonne i DB, saa ingen DDL.
export type QuoteStatus = "awaiting_info" | "draft" | "sent" | "confirmed" | "expired" | "cancelled" | "rejected";

// Quotes table
export const quotes = pgTable("quotes", {
  id: varchar("id", { length: 36 }).primaryKey(),
  quoteNumber: text("quote_number").notNull(),
  customerId: varchar("customer_id", { length: 36 }).notNull(),
  status: text("status").notNull().default("draft"),
  confirmToken: text("confirm_token"),
  // Optional per-quote extra recipients (comma-separated) notified when the
  // resulting contract is signed, in addition to the global list in settings.
  notifyEmails: text("notify_emails"),
  // Kunden oensker aa betale med Vipps-betalingslenke; foelger med til ordren
  // ved bekreftelse slik at henting-flyten krever lenke foer henteinfo sendes.
  paymentViaVippsLink: boolean("payment_via_vipps_link").default(false),

  // Pricing (NOK with 2 decimal places)
  listTotalAmount: numeric("list_total_amount", { precision: 10, scale: 2 }), // Sum av veiledende priser
  totalAmount: numeric("total_amount", { precision: 10, scale: 2 }), // Faktisk totalpris
  agreedTotalPrice: numeric("agreed_total_price", { precision: 10, scale: 2 }), // Avtalt fast pris - null if using item-level discounts
  autoDiscountPercent: integer("auto_discount_percent"), // Auto-calculated when agreedTotalPrice is set
  currency: text("currency").default("NOK"),
  
  // Notes
  internalNotes: text("internal_notes"),
  customerNotes: text("customer_notes"),
  
  // Delivery preferences
  deliveryMethod: text("delivery_method").default("posten"),
  deliveryNotes: text("delivery_notes"),

  // Custom delivery address override (set on this quote)
  hasCustomDelivery: boolean("has_custom_delivery").default(false),
  deliveryAddress: text("delivery_address"),
  deliveryPostalCode: varchar("delivery_postal_code", { length: 10 }),
  deliveryCity: text("delivery_city"),

  // Extra invoice information (all optional — surfaced on invoice)
  hasCustomInvoice: boolean("has_custom_invoice").default(false),
  invoicePurchaseOrder: text("invoice_purchase_order"),
  invoiceReference: text("invoice_reference"),
  invoicePoNumber: text("invoice_po_number"),
  // Fakturamottaker som avviker fra avtaleparten. Settes paa tilbudet (det er
  // der kunden signerer) og foelger med til ordren ved bekreftelse, slik at
  // Fiken fakturerer det selskapet kontrakten oppgir.
  invoiceRecipientCompany: text("invoice_recipient_company"),
  invoiceRecipientOrgNumber: text("invoice_recipient_org_number"),

  // Avtalt betalingsfrist i dager fra fakturadato (29.9.2026: Forsvaret krever
  // 30 dager, og fristen maa staa i det kunden signerer). Tomt = kundekortets
  // frist, ellers arrangementsregelen. Foelger med til ordren ved bekreftelse
  // og styrer kontraktens betalingsbetingelser og Fikens forfall.
  // Se @shared/payment-terms.
  paymentTermsDays: integer("payment_terms_days"),

  // Eksplisitt kundetillatelse for fraktvalg. Null/tom = avtalt frakt er låst;
  // ikke-tom liste = kunden kan velge nøyaktig disse aktive alternativene.
  // Se @shared/offered-shipping for den fail-closed serverregelen.
  offeredShippingOptionIds: json("offered_shipping_option_ids").$type<string[]>(),

  // Avtalt kurér-/budbilpris (eks mva, tur/retur samlet). Kun meningsfull naar
  // valgt fraktalternativ har requiresManualPrice; erstatter da hele den
  // beregnede fraktkosten.
  manualShippingPrice: numeric("manual_shipping_price", { precision: 10, scale: 2 }),

  // Lading er INKLUDERT som standard. Kun naar Fredrik eksplisitt aapner for
  // det, faar kunden velge den bort i bekreftelsen (dagens opt-out-UI).
  chargingCustomerOptional: boolean("charging_customer_optional").default(false),

  // Kontrahenten: personen som signerer paa vegne av kunden, og som er
  // juridisk ansvarlig hvis fakturamottakeren ikke betaler. Foelger med til
  // ordren ved bekreftelse.
  contactPersonName: text("contact_person_name"),
  contactPersonEmail: text("contact_person_email"),
  contactPersonPhone: text("contact_person_phone"),

  // Shipping selections (references to shipping options)
  outboundShippingId: varchar("outbound_shipping_id", { length: 36 }),
  returnShippingId: varchar("return_shipping_id", { length: 36 }),
  numberOfPackages: integer("number_of_packages").default(1),
  
  // Event details
  eventName: text("event_name"),
  eventDate: date("event_date"),
  sendDate: date("send_date"),
  // Dates for extra rental days (Dag 2, Dag 3, ...). Map of dayNumber -> ISO date.
  // Day 1 uses eventDate. Missing entries default to eventDate + (dayNumber - 1) days.
  dayDates: json("day_dates").$type<Record<string, string>>(),
  // Avtalt returfrist: overstyrer den beregnede fristen. Foelger med til
  // ordren ved bekreftelse (se returnDeadlineOverride paa orders).
  returnDeadlineOverride: date("return_deadline_override"),

  // Timestamps
  createdAt: timestamp("created_at").defaultNow(),
  sentAt: timestamp("sent_at"),
  confirmedAt: timestamp("confirmed_at"),
  validUntil: timestamp("valid_until"),

  // Aapnings-sporing: har kunden faktisk aapnet bekreft-tilbud-lenken?
  // Settes KUN av det offentlige token-endepunktet (GET /api/public/quote/:token),
  // aldri av admin-flytene. Vises i tilbudslisten og tilbudsdetaljen.
  firstViewedAt: timestamp("first_viewed_at"),
  viewCount: integer("view_count").default(0),

  // Hvilket lager som skal «eie» ordren (delegering, f.eks. Hoenefoss).
  // Settes i tilbudet og arves av ordren ved bekreftelse; tomt = dagens
  // auto-valg av lager bestaar.
  warehouseId: varchar("warehouse_id", { length: 36 }),

  // Link to order when confirmed
  orderId: varchar("order_id", { length: 36 }),
});

export const insertQuoteSchema = createInsertSchema(quotes).omit({
  id: true,
  quoteNumber: true,
  confirmToken: true,
  createdAt: true,
  sentAt: true,
  confirmedAt: true,
  firstViewedAt: true,
  viewCount: true,
  orderId: true,
});

export type InsertQuote = z.infer<typeof insertQuoteSchema>;
export type Quote = typeof quotes.$inferSelect;

// Quote variants - multiple options in one quote; customer chooses one when confirming
export const quoteVariants = pgTable("quote_variants", {
  id: varchar("id", { length: 36 }).primaryKey(),
  quoteId: varchar("quote_id", { length: 36 }).notNull(),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertQuoteVariantSchema = createInsertSchema(quoteVariants).omit({
  id: true,
  createdAt: true,
});

export type InsertQuoteVariant = z.infer<typeof insertQuoteVariantSchema>;
export type QuoteVariant = typeof quoteVariants.$inferSelect;

// Quote items - line types: product, shipping, washing, charging
export const quoteItems = pgTable("quote_items", {
  id: varchar("id", { length: 36 }).primaryKey(),
  quoteId: varchar("quote_id", { length: 36 }).notNull(),
  variantId: varchar("variant_id", { length: 36 }), // NULL = legacy single-option quote
  lineType: text("line_type").notNull().default("product"), // product, shipping, washing, charging
  productType: text("product_type").notNull(),
  productName: text("product_name").notNull(),
  quantity: integer("quantity").notNull().default(1),
  dayNumber: integer("day_number").notNull().default(1), // Which day this line represents (Dag 1, Dag 2, etc). dayNumber>1 lines excluded from kolli/vask calculations
  quantityUnit: text("quantity_unit"), // Unit type for quantity display: "headset", "kolli", "stk", category name, etc.
  listPrice: numeric("list_price", { precision: 10, scale: 2 }), // Veiledende pris per enhet
  discountPercent: integer("discount_percent").default(0), // 0-100
  unitPrice: numeric("unit_price", { precision: 10, scale: 2 }), // Pris etter rabatt per enhet
  totalPrice: numeric("total_price", { precision: 10, scale: 2 }), // unitPrice * quantity
  isDiscountLocked: boolean("is_discount_locked").default(false), // True if 100% discount should stay fixed
  comment: text("comment"),
  days: integer("days").notNull().default(1),

  // Hourly (timespris) services. When set, the line is billed by time instead of
  // a fixed unit price. Billable time is the sum of these three components,
  // rounded UP to the nearest started half-hour (see roundUpToHalfHourMinutes).
  isHourly: boolean("is_hourly").default(false),
  hourlyRate: numeric("hourly_rate", { precision: 10, scale: 2 }), // Timesats (kr/time), snapshot from service at quote time (editable on quote)
  setupMinutes: integer("setup_minutes").default(0), // Tid for oppmøte (minutter)
  workMinutes: integer("work_minutes").default(0), // Aktuelt arbeid (minutter)
  returnMinutes: integer("return_minutes").default(0), // Tid for retur (minutter)
  adminMinutes: integer("admin_minutes").default(0), // Fast administrasjons-halvtime (minutter)
  billableMinutes: integer("billable_minutes").default(0), // Sum avrundet opp til påbegynt halvtime + admin
  estimatedHours: numeric("estimated_hours", { precision: 10, scale: 2 }), // Estimert antall timer (justeres mot faktisk tid; for fremtidig betalingsintegrasjon)
});

export const insertQuoteItemSchema = createInsertSchema(quoteItems).omit({
  id: true,
});

export type InsertQuoteItem = z.infer<typeof insertQuoteItemSchema>;
export type QuoteItem = typeof quoteItems.$inferSelect;

// Shipments table - multiple shipments per order
export const shipments = pgTable("shipments", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  type: text("type").notNull().default("outbound"),
  carrier: text("carrier").notNull(),
  trackingNumber: text("tracking_number"),
  barcode: text("barcode"),
  returnLabelUrl: text("return_label_url"),
  // QR-lenke til returlappen (Postens bekreftelsesmail). Kunden viser den i
  // skranken og faar fraktbrevet skrevet ut ferdig utfylt. Leveres av
  // posten-manager via webhookens returQrUrls.
  returnQrUrl: text("return_qr_url"),
  status: text("status").notNull().default("created"),
  packageNumber: integer("package_number").default(1),
  lastTrackingStatus: text("last_tracking_status"),
  lastTrackingUpdate: timestamp("last_tracking_update"),
  createdAt: timestamp("created_at").defaultNow(),
  sentAt: timestamp("sent_at"),
  deliveredAt: timestamp("delivered_at"),
  // Customer confirmation fields
  confirmationDeadline: timestamp("confirmation_deadline"), // 4 hours after delivered
  customerConfirmedAt: timestamp("customer_confirmed_at"),
  autoApprovedAt: timestamp("auto_approved_at"),
  confirmationNotes: text("confirmation_notes"), // Notes from customer or auto-approval
});

export const insertShipmentSchema = createInsertSchema(shipments).omit({
  id: true,
  createdAt: true,
  sentAt: true,
  deliveredAt: true,
  lastTrackingUpdate: true,
  confirmationDeadline: true,
  customerConfirmedAt: true,
  autoApprovedAt: true,
});

export type InsertShipment = z.infer<typeof insertShipmentSchema>;
export type Shipment = typeof shipments.$inferSelect;

// Shipment type
export type ShipmentType = "outbound" | "return";
export type ShipmentStatus = "created" | "label_printed" | "picked_up" | "in_transit" | "delivered";

// Order item form schema with pricing
export const orderItemFormSchema = z.object({
  category: z.enum(["rental", "service"]).default("rental"),
  productType: z.string(),
  productName: z.string(),
  quantity: z.number().min(1),
  listPrice: z.coerce.number().min(0).default(0), // Veiledende pris per enhet (NOK)
  agreedPrice: z.coerce.number().min(0).default(0), // Avtalt pris per enhet (NOK)
  discountPercent: z.number().min(0).max(100).default(0), // Rabattprosent
});

export type OrderItemForm = z.infer<typeof orderItemFormSchema>;

// Service types for orders
export const SERVICE_TYPES = [
  { type: "shipping", name: "Frakt" },
  { type: "technician", name: "Tekniker" },
  { type: "personnel", name: "Personell" },
  { type: "washing", name: "Vask" },
  { type: "charging", name: "Lading" },
  { type: "setup", name: "Oppsett" },
  { type: "other_service", name: "Annen tjeneste" },
] as const;

// Form schemas for frontend validation
export const createOrderFormSchema = z.object({
  // Order status (for draft saving)
  status: z.enum(["draft", "created"]).optional(),
  customerName: z.string().min(1, "Kundenavn er påkrevd"),
  customerCompany: z.string().optional(),
  customerEmail: z.string().email("Ugyldig e-postadresse"),
  customerPhone: z.string().optional(),
  customerAddress: z.string().optional(),
  customerPostalCode: z.string().optional(),
  customerCity: z.string().optional(),
  customerWashesEquipment: z.boolean().default(false),
  customerChargesEquipment: z.boolean().default(false),
  // Delivery address override
  hasCustomDelivery: z.boolean().default(false),
  deliveryName: z.string().optional(),
  deliveryAddress: z.string().optional(),
  deliveryPostalCode: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryPhone: z.string().optional(),
  // Invoice information
  hasCustomInvoice: z.boolean().default(false),
  invoiceDepartment: z.string().optional(),
  invoiceReference: z.string().optional(),
  invoiceContactPerson: z.string().optional(),
  invoicePhone: z.string().optional(),
  invoiceEmail: z.string().optional(),
  // Fakturamottaker som avviker fra avtaleparten (sjelden). Lagres uavhengig av
  // hasCustomInvoice, slik at valget ikke kan bli stille nullstilt av et lagre-
  // klikk der avkrysningsboksen over staar av.
  invoiceRecipientCompany: z.string().nullable().optional(),
  invoiceRecipientOrgNumber: optionalOrgNumberSchema,
  contactPersonName: z.string().nullable().optional(),
  contactPersonEmail: z.string().nullable().optional(),
  contactPersonPhone: z.string().nullable().optional(),
  // Event details
  eventName: z.string().optional(),
  eventDate: z.string().optional(), // Made optional for drafts (validated on normal submit via form)
  latestArrivalDate: z.string().optional(),
  sendDate: z.string().optional(), // Made optional for drafts (validated on normal submit via form)
  // Optional dates per extra rental day (key = dayNumber as string, value = ISO date)
  dayDates: z.record(z.string(), z.string()).optional(),
  // Avtalt returfrist (valgfritt): overstyrer beregnet returfrist naar satt.
  returnDeadlineOverride: z.string().optional(),
  numberOfPackages: z.number().min(1).default(1),
  deliveryMethod: z.enum(["pickup", "posten", "bring", "other"]).optional(),
  warehouseId: z.string().optional(),
  deliveryNotes: z.string().optional(),
  // Rental items (leieobjekter)
  items: z.array(z.object({
    productType: z.string(),
    productName: z.string(),
    quantity: z.number().min(1, "Antall må være minst 1"),
    dayNumber: z.number().min(1).optional().default(1),
    comment: z.string().optional(),
    listPrice: z.coerce.number().min(0).default(0),
    agreedPrice: z.coerce.number().min(0).default(0),
    discountPercent: z.number().min(0).max(100).default(0),
    discountType: z.enum(["none", "percent", "amount", "fixed"]).default("none"),
    discountAmount: z.coerce.number().default(0),
  })).min(1, "Minst ett produkt er påkrevd"),
  // Service items (tjenester)
  // Pricing totals
  rentalListTotal: z.coerce.number().default(0),
  rentalAgreedTotal: z.coerce.number().default(0),
  servicesListTotal: z.coerce.number().default(0),
  servicesAgreedTotal: z.coerce.number().default(0),
  totalListPrice: z.coerce.number().default(0),
  totalAgreedPrice: z.coerce.number().default(0),
  discountAmount: z.coerce.number().default(0),
  vatAmount: z.coerce.number().default(0),
  grandTotal: z.coerce.number().default(0),
  
  services: z.array(z.object({
    productType: z.string(),
    productName: z.string(),
    quantity: z.number().min(1, "Antall må være minst 1"),
    dayNumber: z.number().min(1).optional().default(1),
    comment: z.string().optional(),
    listPrice: z.coerce.number().min(0).default(0),
    agreedPrice: z.coerce.number().min(0).default(0),
    discountPercent: z.number().min(0).max(100).default(0),
    discountType: z.enum(["none", "percent", "amount", "fixed"]).default("none"),
    discountAmount: z.coerce.number().default(0),
    quantityLinkType: z.enum(["manual", "kolli", "category", "headset"]).default("manual"),
    quantityLinkCategoryId: z.string().nullable().optional(),
  })).optional().default([]),
  // Shipping - simple two dropdowns (Utsending/Retur)
  outboundShippingId: z.string().nullable().optional(),
  returnShippingId: z.string().nullable().optional(),
  // Pricing - can override section totals
  useAgreedRentalTotal: z.boolean().default(false),
  agreedRentalTotal: z.coerce.number().min(0).optional(), // Avtalt pris for leieobjekter samlet
  useAgreedServicesTotal: z.boolean().default(false),
  agreedServicesTotal: z.number().min(0).optional(), // Avtalt pris for tjenester samlet
  useAgreedGrandTotal: z.boolean().default(false),
  agreedGrandTotal: z.number().min(0).optional(), // Avtalt totalpris for alt
  vatExempt: z.boolean().default(false), // MVA-fritak
})
  // Sendedato var lenge bare paakrevd i klientens onSubmit-handler. Alt som
  // ikke gikk gjennom det skjemaet -- tilbudskonvertering, gjenbestilling,
  // direkte API-kall -- kunne lagre en ordre uten dato, og den ble usynlig i
  // pakkeliste og morgenrapport samtidig (ORD-1052). Kravet hoerer hjemme her,
  // der alle skriveveier passerer. Utkast er fortsatt unntatt: de er halvferdig
  // arbeid og skal kunne lagres uten datoer.
  .refine(
    (data) => data.status === "draft" || Boolean(data.sendDate),
    {
      message: "Sendedato er påkrevd for ordrer som ikke er utkast.",
      path: ["sendDate"],
    },
  )
  // ORD-218 (10.9.2026): 56 tegn i leveringsadressen gikk rett gjennom skjemaet,
  // gjennom kontraktsigneringen og gjennom fraktkoeen, og ble foerst avvist av
  // Postens bestillingsskjema («Adressen kan ikke inneholde mer enn 50 tegn.
  // Bruk adresselinje 2.») -- etter at Fredrik hadde trykket «Book». Regelen
  // ligger paa SKJEMAET, ikke bare i klienten, fordi tilbudskonvertering,
  // gjenbestilling og direkte API-kall alle passerer her (samme grunn som
  // sendedato-kravet over). Utkast er IKKE unntatt: en for lang adresse er
  // feilskrevet data, ikke uferdig arbeid, og skal rettes der den skrives.
  .superRefine((data, ctx) => {
    const customDelivery = data.hasCustomDelivery === true;
    const deliveryAddress = (data.deliveryAddress ?? "").trim();
    if (customDelivery) {
      const problem = checkShippingAddressLength(deliveryAddress);
      if (problem) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem.message, path: ["deliveryAddress"] });
      }
    }
    // Kundeadressen er fraktadressen naar det ikke staar en egen leveringsadresse
    // -- noeyaktig samme fallback som effectiveShippingAddress/resolveBookingRecipient.
    // Vi speiler den i stedet for aa sperre paa kundeadressen alltid: en lang
    // registrert fakturaadresse som ALDRI havner paa etiketten skal ikke kunne
    // laase ordren.
    if (!(customDelivery && deliveryAddress)) {
      const problem = checkShippingAddressLength(data.customerAddress, { label: "Kundeadressen" });
      if (problem) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem.message, path: ["customerAddress"] });
      }
    }
  });

export type CreateOrderForm = z.infer<typeof createOrderFormSchema>;

export const returnFormSchema = z.object({
  headsetsReturned: z.number().min(0, "Antall kan ikke være negativt"),
  washedConfirmed: z.boolean(),
  chargedConfirmed: z.boolean(),
});

export type ReturnForm = z.infer<typeof returnFormSchema>;

export const inspectionFormSchema = z.object({
  firstCount: z.number().min(0),
  secondCount: z.number().min(0).optional(),
  washedStatus: z.boolean(),
  transmittersOk: z.boolean(),
  accessoriesOk: z.boolean(),
  notes: z.string().optional(),
});

export type InspectionForm = z.infer<typeof inspectionFormSchema>;

// Customer form schema
export const createCustomerFormSchema = z.object({
  name: z.string().min(1, "Navn er påkrevd"),
  isPrivatePerson: z.boolean().default(false),
  company: z.string().optional(),
  orgNumber: z.string().optional(),
  contactRole: z.string().optional(), // Role of contact person in company
  email: z.string().email("Ugyldig e-postadresse"),
  phone: z.string().optional(),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  
  // Delivery address override
  hasCustomDelivery: z.boolean().default(false),
  deliveryName: z.string().optional(),
  deliveryAddress: z.string().optional(),
  deliveryPostalCode: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryPhone: z.string().optional(),
  
  // Invoice information override
  hasCustomInvoice: z.boolean().default(false),
  invoiceMethod: z.enum(["ehf", "email", "post"]).default("email"),
  invoiceEhfReference: z.string().optional(),
  invoiceDepartment: z.string().optional(),
  invoiceReference: z.string().optional(),
  invoiceContactPerson: z.string().optional(),
  invoicePhone: z.string().optional(),
  invoiceEmail: z.string().email("Ugyldig e-postadresse").optional().or(z.literal("")),
  invoiceAddress: z.string().optional(),
  invoicePostalCode: z.string().optional(),
  invoiceCity: z.string().optional(),
  paymentTermsDays: z.number().int().min(0, "Betalingsfristen kan ikke være negativ").nullable().optional(),
  
  washesEquipment: z.boolean().default(false),
  chargesEquipment: z.boolean().default(false),
  notes: z.string().optional(),
}).refine((data) => {
  // If custom delivery is enabled, require delivery fields
  if (data.hasCustomDelivery) {
    return !!data.deliveryName && !!data.deliveryAddress && !!data.deliveryPhone;
  }
  return true;
}, {
  message: "Vennligst fyll ut alle leveringsfelter",
  path: ["deliveryName"],
});
// Merk: ingen krav om invoiceAddress ved hasCustomInvoice. Skjemaet har ikke et
// fakturaadresse-felt (kun metode/EHF-ref/avdeling/referanse/kontaktperson/
// telefon/fakturamail), saa et slikt krav blokkerte lagring stille. EHF/e-post-
// fakturering trenger uansett ikke postadresse.

export type CreateCustomerForm = z.infer<typeof createCustomerFormSchema>;

// Quote form schema (all fields sent by client when creating/updating quote must be listed so Zod does not strip them)
const quoteItemFormSchema = z.object({
  productType: z.string(),
  productName: z.string(),
  quantity: z.number().min(1),
  unitPrice: z.coerce.number().optional(),
  agreedPrice: z.coerce.number().optional(),
  listPrice: z.coerce.number().optional(),
  totalPrice: z.coerce.number().optional(),
  discountPercent: z.coerce.number().optional(),
  dayNumber: z.coerce.number().optional(),
  comment: z.string().optional(),
  quantityUnit: z.string().nullable().optional(),
  lineType: z.enum(["product", "service", "shipping", "washing", "charging"]).optional(),
  // Hourly (timespris) service fields
  isHourly: z.boolean().optional(),
  hourlyRate: z.coerce.number().optional(),
  setupMinutes: z.coerce.number().optional(),
  workMinutes: z.coerce.number().optional(),
  returnMinutes: z.coerce.number().optional(),
  estimatedHours: z.coerce.number().optional(),
});

export const createQuoteFormSchema = z.object({
  customerId: z.string().min(1, "Kunde er påkrevd"),
  totalAmount: z.coerce.number().optional(),
  listTotalAmount: z.coerce.number().optional(),
  agreedTotalPrice: z.coerce.number().nullable().optional(),
  // Eksplisitt signal fra skjemaet: en av totalpris-overstyringene er aktiv.
  // Serveren bruker dette til å sikre at totalAmount også lagres som avtalt pris.
  priceOverrideApplied: z.boolean().optional().default(false),
  currency: z.string().default("NOK"),
  internalNotes: z.string().optional(),
  customerNotes: z.string().optional(),
  deliveryMethod: z.enum(["pickup", "posten", "bring", "other"]).nullable().optional(),
  deliveryNotes: z.string().nullable().optional(),
  // Custom delivery address override
  hasCustomDelivery: z.boolean().optional(),
  deliveryAddress: z.string().nullable().optional(),
  deliveryPostalCode: z.string().nullable().optional(),
  deliveryCity: z.string().nullable().optional(),
  // Extra invoice info (all optional)
  hasCustomInvoice: z.boolean().optional(),
  invoicePurchaseOrder: z.string().nullable().optional(),
  invoiceReference: z.string().nullable().optional(),
  invoicePoNumber: z.string().nullable().optional(),
  // Fakturamottaker som avviker fra avtaleparten (sjelden); foelger med til
  // ordren ved bekreftelse.
  invoiceRecipientCompany: z.string().nullable().optional(),
  invoiceRecipientOrgNumber: optionalOrgNumberSchema,
  // Avtalt betalingsfrist i dager fra fakturadato; tomt = kundekortet/standard.
  paymentTermsDays: z.number().int().min(0, "Betalingsfristen kan ikke være negativ").nullable().optional(),
  contactPersonName: z.string().nullable().optional(),
  contactPersonEmail: z.string().nullable().optional(),
  contactPersonPhone: z.string().nullable().optional(),
  // Null/tom låser frakten. En ikke-tom liste er både tillatelsen og allowlisten.
  offeredShippingOptionIds: z.array(z.string()).nullable().optional(),
  manualShippingPrice: z.union([z.string(), z.number()]).nullable().optional(),
  chargingCustomerOptional: z.boolean().optional(),
  validUntil: z.string().optional(),
  outboundShippingId: z.string().nullable().optional(),
  returnShippingId: z.string().nullable().optional(),
  numberOfPackages: z.coerce.number().nullable().optional(),
  eventName: z.string().optional(),
  eventDate: z.string().optional(),
  sendDate: z.string().optional(),
  // Extra recipients (comma-separated) notified when the resulting contract is
  // signed, on top of the global list in settings.
  notifyEmails: z.string().nullable().optional(),
  // Kunden betaler med Vipps-betalingslenke (foelger med til ordren).
  paymentViaVippsLink: z.boolean().optional(),
  // Lager som skal «eie» ordren (delegering); arves av ordren ved bekreftelse.
  warehouseId: z.string().nullable().optional(),
  // Optional dates per extra rental day (key = dayNumber as string, value = ISO date)
  dayDates: z.record(z.string(), z.string()).optional(),
  // Avtalt returfrist (valgfritt): foelger med til ordren ved bekreftelse.
  returnDeadlineOverride: z.string().nullable().optional(),
  // Terminaltilstander (sent/confirmed/rejected/expired/cancelled/awaiting_info)
  // settes KUN av sine dedikerte endepunkter (/send, /accept, /cancel, osv).
  // Klienten (quote-form.tsx) sender aldri noe annet enn "draft" (eller
  // utelater feltet) via dette skjemaet -- uten denne begrensningen kunne en
  // klient sette status="confirmed"/"rejected" og hoppe forbi
  // tryMarkQuoteConfirmed/Rejected-vaktene.
  status: z.enum(["draft"]).optional(),
  // Legacy: single set of items (one option)
  items: z.array(quoteItemFormSchema).optional(),
  // Multiple variants: customer chooses one when confirming
  variants: z.array(z.object({
    id: z.string().optional(),
    name: z.string().min(1, "Alternativnavn er påkrevd"),
    description: z.string().optional(),
    items: z.array(quoteItemFormSchema).min(1, "Minst ett produkt per alternativ"),
  })).optional(),
}).refine(
  (data) => {
    if (data.variants && data.variants.length > 0) return true;
    if (data.items && data.items.length > 0) return true;
    return false;
  },
  { message: "Legg til minst ett produkt (enten under «Ett tilbud» eller under alternativer)", path: ["items"] }
).superRefine((data, ctx) => {
  // Samme grense som paa ordren. ORD-218 KOM fra et tilbud, saa fanges den ikke
  // her, foelger den lange adressen med inn i ordren ved bekreftelse -- og da er
  // kontrakten allerede signert. Tilbudet har ingen `customerAddress`
  // (kundeadressen kommer fra kundekortet via customerId), saa kun det
  // eksplisitte leveringsfeltet kan sjekkes her.
  if (data.hasCustomDelivery !== true) return;
  const problem = checkShippingAddressLength(data.deliveryAddress);
  if (problem) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem.message, path: ["deliveryAddress"] });
  }
});

export type CreateQuoteForm = z.infer<typeof createQuoteFormSchema>;

// Warehouses table
export const warehouses = pgTable("warehouses", {
  id: varchar("id", { length: 36 }).primaryKey(),
  name: text("name").notNull(),
  code: varchar("code", { length: 20 }).notNull(),
  address: text("address"),
  isActive: boolean("is_active").default(true),
  isDefault: boolean("is_default").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertWarehouseSchema = createInsertSchema(warehouses).omit({
  id: true,
  createdAt: true,
});

export type InsertWarehouse = z.infer<typeof insertWarehouseSchema>;
export type Warehouse = typeof warehouses.$inferSelect;

// Inventory per product per warehouse
export const warehouseInventory = pgTable("warehouse_inventory", {
  id: varchar("id", { length: 36 }).primaryKey(),
  warehouseId: varchar("warehouse_id", { length: 36 }).notNull(),
  productId: varchar("product_id", { length: 36 }).notNull(),
  availableQuantity: integer("available_quantity").default(0),
  reservedQuantity: integer("reserved_quantity").default(0),
  inTransitQuantity: integer("in_transit_quantity").default(0),
  withCustomersQuantity: integer("with_customers_quantity").default(0),
  lastCountAt: timestamp("last_count_at"),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertWarehouseInventorySchema = createInsertSchema(warehouseInventory).omit({
  id: true,
  updatedAt: true,
});

export type InsertWarehouseInventory = z.infer<typeof insertWarehouseInventorySchema>;
export type WarehouseInventory = typeof warehouseInventory.$inferSelect;

// Warehouse transfers (move equipment between warehouses)
export type TransferStatus = "pending" | "in_transit" | "completed" | "cancelled";

export const warehouseTransfers = pgTable("warehouse_transfers", {
  id: varchar("id", { length: 36 }).primaryKey(),
  fromWarehouseId: varchar("from_warehouse_id", { length: 36 }).notNull(),
  toWarehouseId: varchar("to_warehouse_id", { length: 36 }).notNull(),
  status: text("status").notNull().default("pending"),
  notes: text("notes"),
  initiatedBy: varchar("initiated_by", { length: 36 }),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
});

export const insertWarehouseTransferSchema = createInsertSchema(warehouseTransfers).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});

export type InsertWarehouseTransfer = z.infer<typeof insertWarehouseTransferSchema>;
export type WarehouseTransfer = typeof warehouseTransfers.$inferSelect;

// Transfer items
export const warehouseTransferItems = pgTable("warehouse_transfer_items", {
  id: varchar("id", { length: 36 }).primaryKey(),
  transferId: varchar("transfer_id", { length: 36 }).notNull(),
  productId: varchar("product_id", { length: 36 }).notNull(),
  quantity: integer("quantity").notNull().default(1),
});

export const insertWarehouseTransferItemSchema = createInsertSchema(warehouseTransferItems).omit({
  id: true,
});

export type InsertWarehouseTransferItem = z.infer<typeof insertWarehouseTransferItemSchema>;
export type WarehouseTransferItem = typeof warehouseTransferItems.$inferSelect;

// Shipping options - configurable shipping methods per warehouse
export type ShippingPricingType = "kolli" | "headset" | "fastpris";

export const shippingOptions = pgTable("shipping_options", {
  id: varchar("id", { length: 36 }).primaryKey(),
  name: text("name").notNull(),
  carrier: text("carrier"), // posten, bring, etc.
  warehouseId: varchar("warehouse_id", { length: 36 }), // null = available from all warehouses
  baseCost: numeric("base_cost", { precision: 10, scale: 2 }).default("0.00"), // Cost in NOK
  overweightSurcharge: numeric("overweight_surcharge", { precision: 10, scale: 2 }).default("0.00"), // Per-kolli overweight fee (NOK), both ways
  pricingType: varchar("pricing_type", { length: 20 }).default("fastpris"), // kolli, headset, fastpris
  description: text("description"),
  estimatedDays: integer("estimated_days"),
  isActive: boolean("is_active").default(true),
  isDefault: boolean("is_default").default(false),
  // Budbil/transport/egen sjaafoer: prisen varierer per selskap og arrangement
  // og MAA legges inn per tilbud. Flagget driver prisfeltet i tilbudsskjemaet
  // og sendegaten som nekter aa sende uten pris.
  requiresManualPrice: boolean("requires_manual_price").default(false),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertShippingOptionSchema = createInsertSchema(shippingOptions).omit({
  id: true,
  createdAt: true,
});

export type InsertShippingOption = z.infer<typeof insertShippingOptionSchema>;
export type ShippingOption = typeof shippingOptions.$inferSelect;

// Inventory counts (varetelling) - physical inventory counts
export type InventoryCountStatus = "in_progress" | "completed" | "cancelled";

export const inventoryCounts = pgTable("inventory_counts", {
  id: varchar("id", { length: 36 }).primaryKey(),
  warehouseId: varchar("warehouse_id", { length: 36 }).notNull(),
  performedById: varchar("performed_by_id", { length: 36 }),
  performedByName: text("performed_by_name"),
  status: text("status").notNull().default("in_progress"),
  notes: text("notes"),
  totalDiscrepancy: integer("total_discrepancy").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
});

export const insertInventoryCountSchema = createInsertSchema(inventoryCounts).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});

export type InsertInventoryCount = z.infer<typeof insertInventoryCountSchema>;
export type InventoryCount = typeof inventoryCounts.$inferSelect;

// Inventory count items - individual product counts in a varetelling
export const inventoryCountItems = pgTable("inventory_count_items", {
  id: varchar("id", { length: 36 }).primaryKey(),
  countId: varchar("count_id", { length: 36 }).notNull(),
  productId: varchar("product_id", { length: 36 }).notNull(),
  productName: text("product_name"),
  expectedQuantity: integer("expected_quantity").default(0),
  actualQuantity: integer("actual_quantity").default(0),
  variance: integer("variance").default(0),
});

export const insertInventoryCountItemSchema = createInsertSchema(inventoryCountItems).omit({
  id: true,
});

export type InsertInventoryCountItem = z.infer<typeof insertInventoryCountItemSchema>;
export type InventoryCountItem = typeof inventoryCountItems.$inferSelect;

// Inventory adjustments - audit log for all stock movements
export type AdjustmentType = "receiving" | "count" | "manual" | "discrepancy" | "order_sent" | "order_returned";

export const inventoryAdjustments = pgTable("inventory_adjustments", {
  id: varchar("id", { length: 36 }).primaryKey(),
  warehouseId: varchar("warehouse_id", { length: 36 }).notNull(),
  productId: varchar("product_id", { length: 36 }).notNull(),
  adjustmentType: text("adjustment_type").notNull(),
  quantityDelta: integer("quantity_delta").notNull().default(0),
  referenceId: varchar("reference_id", { length: 36 }),
  reason: text("reason"),
  createdById: varchar("created_by_id", { length: 36 }),
  createdByName: text("created_by_name"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertInventoryAdjustmentSchema = createInsertSchema(inventoryAdjustments).omit({
  id: true,
  createdAt: true,
});

export type InsertInventoryAdjustment = z.infer<typeof insertInventoryAdjustmentSchema>;
export type InventoryAdjustment = typeof inventoryAdjustments.$inferSelect;

// Discrepancy cases - tracks issues from returns (missing items, unwashed equipment)
export type DiscrepancyCaseType = "missing_return" | "wash_not_ordered" | "damaged" | "other";
export type DiscrepancyCaseStatus = "open" | "resolved" | "closed";

export const discrepancyCases = pgTable("discrepancy_cases", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }),
  // Kobler saken til issue-raden som mottaket opprettet. Historiske saker har null.
  sourceIssueId: varchar("source_issue_id", { length: 36 }),
  orderNumber: text("order_number"),
  warehouseId: varchar("warehouse_id", { length: 36 }),
  caseType: text("case_type").notNull(),
  status: text("status").notNull().default("open"),
  notes: text("notes"),
  reportedById: varchar("reported_by_id", { length: 36 }),
  reportedByName: text("reported_by_name"),
  reportedAt: timestamp("reported_at").defaultNow(),
  closedById: varchar("closed_by_id", { length: 36 }),
  closedByName: text("closed_by_name"),
  closedAt: timestamp("closed_at"),
  closedNotes: text("closed_notes"),
  // --- Gebyrfakturaen i Fiken -------------------------------------------
  // Uten denne koblingen var fakturaen usynlig for systemet: ORD-172 fikk
  // faktura 10720 laget direkte i Fiken, og saken viste ingenting. Referansen
  // "<ordrenr>-AVVIK" fanger bare fakturaer knappen har laget.
  feeInvoiceNumber: text("fee_invoice_number"),
  /**
   * Fikens id for gebyrfakturaen. Saa lenge feeInvoiceStatus er "draft" er
   * dette en KLADD-id (drafts-endepunktet), ellers Fikens invoiceId. De to
   * ligger i hver sin id-serie, saa oppslag paa id gjoeres kun naar statusen
   * viser at fakturaen er ute av kladdstadiet.
   */
  feeInvoiceFikenId: text("fee_invoice_fiken_id"),
  /** draft | sent | paid | credited | unknown. Null = ingen faktura koblet. */
  feeInvoiceStatus: text("fee_invoice_status"),
  feeInvoiceCheckedAt: timestamp("fee_invoice_checked_at"),
});

/** Statusene feeInvoiceStatus kan ha. Null i basen betyr «ingen faktura koblet». */
export type DiscrepancyFeeInvoiceStatus = "draft" | "sent" | "paid" | "credited" | "unknown";

export const insertDiscrepancyCaseSchema = createInsertSchema(discrepancyCases).omit({
  id: true,
  reportedAt: true,
  closedAt: true,
});

export type InsertDiscrepancyCase = z.infer<typeof insertDiscrepancyCaseSchema>;
export type DiscrepancyCase = typeof discrepancyCases.$inferSelect;

// Discrepancy items - individual products in a discrepancy case
export const discrepancyItems = pgTable("discrepancy_items", {
  id: varchar("id", { length: 36 }).primaryKey(),
  caseId: varchar("case_id", { length: 36 }).notNull(),
  productId: varchar("product_id", { length: 36 }),
  productType: text("product_type").notNull(),
  productName: text("product_name").notNull(),
  expectedQuantity: integer("expected_quantity").default(0),
  actualQuantity: integer("actual_quantity").default(0),
  variance: integer("variance").default(0),
});

export const insertDiscrepancyItemSchema = createInsertSchema(discrepancyItems).omit({
  id: true,
});

export type InsertDiscrepancyItem = z.infer<typeof insertDiscrepancyItemSchema>;
export type DiscrepancyItem = typeof discrepancyItems.$inferSelect;

// Receiving sessions - tracks the receiving process for a return
export type ReceivingSessionStatus = "scanning" | "weighing" | "counting" | "confirming" | "completed";

export const receivingSessions = pgTable("receiving_sessions", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  warehouseId: varchar("warehouse_id", { length: 36 }),
  status: text("status").notNull().default("scanning"),
  trackingNumber: text("tracking_number"),
  weightPhotoUrl: text("weight_photo_url"), // Photo of package on scale before opening
  packageWeight: doublePrecision("package_weight"), // Weight in kg
  washingStatus: text("washing_status"), // "approved", "needs_washing", "not_applicable"
  chargingStatus: text("charging_status"), // "approved", "needs_charging", "not_applicable"
  notes: text("notes"),
  receivedBy: varchar("received_by", { length: 36 }),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
});

export const insertReceivingSessionSchema = createInsertSchema(receivingSessions).omit({
  id: true,
  createdAt: true,
  completedAt: true,
});

export type InsertReceivingSession = z.infer<typeof insertReceivingSessionSchema>;
export type ReceivingSession = typeof receivingSessions.$inferSelect;

// Receiving photos - allows up to 10+ photos for unwashed equipment documentation
export type ReceivingPhotoType = "weight" | "contents" | "unwashed" | "damage" | "other";

export const receivingPhotos = pgTable("receiving_photos", {
  id: varchar("id", { length: 36 }).primaryKey(),
  sessionId: varchar("session_id", { length: 36 }).notNull(),
  orderId: varchar("order_id", { length: 36 }).notNull(),
  photoType: text("photo_type").notNull().default("contents"),
  url: text("url").notNull(),
  description: text("description"),
  uploadedBy: varchar("uploaded_by", { length: 36 }),
  uploadedAt: timestamp("uploaded_at").defaultNow(),
});

export const insertReceivingPhotoSchema = createInsertSchema(receivingPhotos).omit({
  id: true,
  uploadedAt: true,
});

export type InsertReceivingPhoto = z.infer<typeof insertReceivingPhotoSchema>;
export type ReceivingPhoto = typeof receivingPhotos.$inferSelect;

// Admin notifications for discrepancies
export type NotificationType = "missing_equipment" | "unwashed_equipment" | "damaged_equipment" | "count_mismatch" | "other";
export type NotificationPriority = "low" | "medium" | "high" | "critical";

export const adminNotifications = pgTable("admin_notifications", {
  id: varchar("id", { length: 36 }).primaryKey(),
  orderId: varchar("order_id", { length: 36 }),
  type: text("type").notNull(),
  priority: text("priority").notNull().default("medium"),
  title: text("title").notNull(),
  description: text("description"),
  metadata: json("metadata"), // Extra info like missing counts, photo URLs, etc.
  requiresAction: boolean("requires_action").default(true),
  resolved: boolean("resolved").default(false),
  resolvedBy: varchar("resolved_by", { length: 36 }),
  resolvedAt: timestamp("resolved_at"),
  resolvedNotes: text("resolved_notes"),
  createdBy: varchar("created_by", { length: 36 }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertAdminNotificationSchema = createInsertSchema(adminNotifications).omit({
  id: true,
  createdAt: true,
  resolvedAt: true,
});

export type InsertAdminNotification = z.infer<typeof insertAdminNotificationSchema>;
export type AdminNotification = typeof adminNotifications.$inferSelect;

// Product Categories - locked database categories for linking products to services
export const productCategories = pgTable("product_categories", {
  id: varchar("id", { length: 36 }).primaryKey(),
  name: varchar("name", { length: 100 }).notNull(),
  description: text("description"),
  sortOrder: integer("sort_order").default(0),
  isActive: boolean("is_active").default(true),
  isLocked: boolean("is_locked").default(false),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertProductCategorySchema = createInsertSchema(productCategories).omit({
  id: true,
  createdAt: true,
});

export type InsertProductCategory = z.infer<typeof insertProductCategorySchema>;
export type ProductCategory = typeof productCategories.$inferSelect;

// Products table for dynamic product catalog
export const products = pgTable("products", {
  id: varchar("id", { length: 36 }).primaryKey(),
  type: varchar("type", { length: 50 }).notNull(),
  name: text("name").notNull(),
  description: text("description"),
  sku: varchar("sku", { length: 50 }),
  category: varchar("category", { length: 50 }), // Legacy free-text field
  categoryId: varchar("category_id", { length: 36 }), // Foreign key to productCategories
  unitPrice: numeric("unit_price", { precision: 10, scale: 2 }).default("0.00"),
  stockQuantity: integer("stock_quantity").default(0),
  isActive: boolean("is_active").default(true),
  sortOrder: integer("sort_order").default(0),
  // Weight ratio relative to 1 headset. E.g., charger=2 (uses space of 2 headsets), transmitter=6, adapter=0.3
  weightRatio: doublePrecision("weight_ratio").default(1),
  // Whether this product can be linked to services for quantity calculation
  canLinkToService: boolean("can_link_to_service").default(false),
  // Product image URL
  imageUrl: text("image_url"),
  // Product features/specifications as JSON array of strings
  features: json("features").$type<string[]>(),
  // Free text for additional product info
  freeText: text("free_text"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertProductSchema = createInsertSchema(products).omit({
  id: true,
  createdAt: true,
});

export type InsertProduct = z.infer<typeof insertProductSchema>;
export type Product = typeof products.$inferSelect;

// Standard service categories
export const SERVICE_CATEGORIES = {
  transport: "Transport",
  rigging: "Rigging",
  support: "Support",
  cleaning: "Rengjøring",
  charging: "Lading",
  equipment: "Utstyr",
  other: "Annet",
} as const;

export type ServiceCategory = keyof typeof SERVICE_CATEGORIES;
export const serviceCategoryValues = Object.keys(SERVICE_CATEGORIES) as ServiceCategory[];

// Services table for standard services (tjenester)
// Services can be linked to kolli count, product categories, or fixed price
export const services = pgTable("services", {
  id: varchar("id", { length: 36 }).primaryKey(),
  name: text("name").notNull(),
  description: text("description"),
  // Standard category for the service (e.g., "cleaning", "transport")
  category: varchar("category", { length: 50 }).default("other"),
  // Pricing method: "fixed" = fast pris, "per_kolli" = per kolli, "per_category" = per product category
  pricingMethod: varchar("pricing_method", { length: 20 }).notNull().default("fixed"),
  // For per_category pricing, link to a product category (from productCategories table)
  // Price is multiplied by quantity of products in that category in the order
  linkedCategoryId: varchar("linked_category_id", { length: 36 }),
  // Legacy fields - kept for backwards compatibility
  linkedProductTypes: json("linked_product_types").$type<string[]>(),
  linkedProductType: varchar("linked_product_type", { length: 50 }),
  // Base price (NOK with 2 decimal places)
  unitPrice: numeric("unit_price", { precision: 10, scale: 2 }).default("0.00"),
  isActive: boolean("is_active").default(true),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertServiceSchema = createInsertSchema(services).omit({
  id: true,
  createdAt: true,
});

export type InsertService = z.infer<typeof insertServiceSchema>;
export type Service = typeof services.$inferSelect;

// Tjeneste/frakt-alternativ der unitPrice er konvertert fra Drizzle sin
// numeric-streng til et faktisk tall (NOK). Brukes der klienten allerede har
// gjort konverteringen (f.eks. skjemaenes tjeneste/frakt-lister) slik at
// forbrukere ikke trenger aa anta at unitPrice fortsatt kan vaere en streng.
export type ServiceWithNumericPrice = Omit<Service, "unitPrice"> & { unitPrice: number };

// Service pricing method labels
export const SERVICE_PRICING_METHODS = {
  fixed: "Fast pris",
  per_hour: "Timespris",
  per_kolli: "Per kolli",
  per_category: "Per produktkategori",
  // Legacy pricing methods (for backwards compatibility display)
  per_headset: "Per headset",
  per_product: "Per produkt(er)",
} as const;

// Billing granularity for hourly (timespris) services: we only invoice whole,
// started half-hours (30 min), never per minute.
export const BILLING_INCREMENT_MINUTES = 30;

// A fixed administration charge of one started half-hour is added to every
// hourly assignment (described in the contract terms). It is surfaced when the
// assignment is completed and always included in the total calculation.
export const BILLING_ADMIN_MINUTES = 30;

/** Round a duration in minutes UP to the nearest started half-hour. */
export function roundUpToHalfHourMinutes(totalMinutes: number): number {
  const m = Math.max(0, Math.ceil(Number(totalMinutes) || 0));
  if (m === 0) return 0;
  return Math.ceil(m / BILLING_INCREMENT_MINUTES) * BILLING_INCREMENT_MINUTES;
}

/**
 * Compute an hourly service line: arrival (oppmøte) + work (arbeid) + return (retur),
 * billed as started half-hours at the given hourly rate, plus a fixed
 * administration half-hour (unless explicitly disabled).
 */
export function computeHourlyLine(input: {
  setupMinutes?: number | null;
  workMinutes?: number | null;
  returnMinutes?: number | null;
  hourlyRate?: number | string | null;
  includeAdmin?: boolean;
}): {
  rawMinutes: number;
  workBillableMinutes: number;
  adminMinutes: number;
  billableMinutes: number;
  billableHours: number;
  total: number;
} {
  const setup = Math.max(0, Number(input.setupMinutes) || 0);
  const work = Math.max(0, Number(input.workMinutes) || 0);
  const ret = Math.max(0, Number(input.returnMinutes) || 0);
  const rawMinutes = setup + work + ret;
  const workBillableMinutes = roundUpToHalfHourMinutes(rawMinutes);
  const includeAdmin = input.includeAdmin !== false;
  const adminMinutes = includeAdmin ? BILLING_ADMIN_MINUTES : 0;
  const billableMinutes = workBillableMinutes + adminMinutes;
  const billableHours = billableMinutes / 60;
  const rate = Number(input.hourlyRate) || 0;
  const total = Math.round(billableHours * rate * 100) / 100;
  return { rawMinutes, workBillableMinutes, adminMinutes, billableMinutes, billableHours, total };
}

// Default products (fallback if no products in database)
export const DEFAULT_PRODUCTS: { type: string; name: string; }[] = [
  { type: "headset", name: "Headset" },
  { type: "transmitter", name: "Sender" },
  { type: "charger_16", name: "16-kontakts lader" },
  { type: "adapter_lightning", name: "Lightning-overgang" },
  { type: "adapter_usbc", name: "USB-C overgang" },
];

// For backwards compatibility
export const PRODUCTS = DEFAULT_PRODUCTS;

// Status labels
export const STATUS_LABELS: Record<OrderStatus, string> = {
  draft: "Utkast",
  created: "Opprettet",
  packed: "Pakket",
  sent: "Sendt",
  delivered: "Hos kunde",
  received: "Mottatt",
  return_initiated: "Retur startet",
  return_sent: "På vei tilbake",
  return_received: "Retur mottatt",
  inspected: "Kontrollert",
  completed: "Fullført",
  cancelled: "Kansellert",
};

/** Kunden slik lageret kjenner den: firma naar det finnes, ellers personen.
 *  Samme regel som arbeidslista (pickup-workboard) bruker, saa ordrenummeret
 *  aldri staar alene i en dialog -- «ORD-1050» sier ingenting naar du har
 *  fem esker foran deg, «ORD-1050 · Welltec» gjoer det. */
export function orderCustomerLabel(order: {
  customerCompany?: string | null;
  customerName?: string | null;
}): string {
  return (order.customerCompany || order.customerName || "").trim();
}

/** Returnerer visningsetikett for ordrestatus.
 *  Henteordrer med status "sent" vises som "Hentet" etter faktisk henting,
 *  ellers "Klar for henting". pickedUpAt er valgfritt for eldre kallsteder.
 */
export function orderStatusLabel(order: {
  deliveryMethod?: string | null;
  status: string;
  pickedUpAt?: string | Date | null;
  pickedUp?: boolean | null;
}): string {
  if (order.status === "sent" && order.deliveryMethod === "pickup") {
    if (order.pickedUpAt || order.pickedUp) return "Hentet";
    return "Klar for henting";
  }
  return STATUS_LABELS[order.status as OrderStatus] ?? order.status;
}

// Delivery method labels
export const DELIVERY_LABELS: Record<DeliveryMethod, string> = {
  pickup: "Hent selv",
  posten: "Posten",
  bring: "Bring",
  other: "Annet",
};

// Quote status labels
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  awaiting_info: "Venter på avtaleinfo",
  draft: "Utkast",
  sent: "Sendt",
  confirmed: "Bekreftet",
  expired: "Utløpt",
  cancelled: "Kansellert",
  rejected: "Avvist",
};

// Contract statuses
export type ContractStatus = "draft" | "pending_signature" | "signed" | "cancelled" | "expired";

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: "Utkast",
  pending_signature: "Venter på signatur",
  signed: "Signert",
  cancelled: "Kansellert",
  expired: "Utløpt",
};

// Contract terms templates - standard terms that can be customized per contract
export const contractTermsTemplates = pgTable("contract_terms_templates", {
  id: varchar("id", { length: 36 }).primaryKey(),
  name: text("name").notNull(),
  content: text("content").notNull(), // Standard contract text
  isDefault: boolean("is_default").default(false),
  isActive: boolean("is_active").default(true),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertContractTermsTemplateSchema = createInsertSchema(contractTermsTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertContractTermsTemplate = z.infer<typeof insertContractTermsTemplateSchema>;
export type ContractTermsTemplate = typeof contractTermsTemplates.$inferSelect;

// Contracts table - created from confirmed quotes
export const contracts = pgTable("contracts", {
  id: varchar("id", { length: 36 }).primaryKey(),
  contractNumber: text("contract_number").notNull(),
  quoteId: varchar("quote_id", { length: 36 }).notNull(),
  customerId: varchar("customer_id", { length: 36 }).notNull(),
  orderId: varchar("order_id", { length: 36 }), // Link to order when created
  
  status: text("status").notNull().default("draft"),
  
  // Contract content
  terms: text("terms").notNull(), // Final contract terms text (editable from template)
  description: text("description"), // Extra notes/description field for the contract
  paymentTerms: text("payment_terms"), // Payment terms (e.g., "14 dager før arrangement")
  
  // Services included (from quote/customer)
  washingIncluded: boolean("washing_included").default(false),
  chargingIncluded: boolean("charging_included").default(false),
  
  // PDF generation
  pdfUrl: text("pdf_url"),
  pdfGeneratedAt: timestamp("pdf_generated_at"),

  // Document integrity (juridisk audit K2): frozen snapshot of exactly what was
  // signed, captured at signing time. signedTermsSnapshot is the immutable copy
  // of the terms text (the mutable `terms` column above can diverge); the hash
  // is SHA-256 over the canonical signed payload (terms + signer + signature +
  // timestamp). Together they prove WHAT was signed in a dispute.
  signedTermsSnapshot: text("signed_terms_snapshot"),
  signedContentHash: text("signed_content_hash"),

  // Timestamps
  createdAt: timestamp("created_at").defaultNow(),
  sentAt: timestamp("sent_at"),
  signedAt: timestamp("signed_at"),
  expiresAt: timestamp("expires_at"),
  lastReminderSentAt: timestamp("last_reminder_sent_at"),
  
  // Created by
  createdBy: varchar("created_by", { length: 36 }),
});

export const insertContractSchema = createInsertSchema(contracts).omit({
  id: true,
  contractNumber: true,
  createdAt: true,
  sentAt: true,
  signedAt: true,
  pdfGeneratedAt: true,
  lastReminderSentAt: true,
});

export type InsertContract = z.infer<typeof insertContractSchema>;
export type Contract = typeof contracts.$inferSelect;

// Contract signatures - e-signature records
export const contractSignatures = pgTable("contract_signatures", {
  id: varchar("id", { length: 36 }).primaryKey(),
  contractId: varchar("contract_id", { length: 36 }).notNull(),
  signingId: varchar("signing_id", { length: 20 }), // Unique human-readable ID per signature (e.g. SIG-A1B2C3D4E5F6)
  
  // Signer info
  signerName: text("signer_name").notNull(),
  signerEmail: text("signer_email").notNull(),
  signerRole: text("signer_role"), // e.g., "Kunde", "Utleier"
  
  // Signature data
  signatureImage: text("signature_image"), // Base64 encoded signature image (text for large images)
  signatureMethod: text("signature_method").default("mouse"), // "mouse", "touch", "typed"
  
  // Verification
  verificationCode: text("verification_code"),
  // Engangs-token bak «Bekreft e-postadressen min»-lenken i kodemailen.
  // Samme levetid som koden (VERIFICATION_CODE_TTL_MS regnet fra
  // verificationSentAt); verificationTokenUsedAt settes ved første bruk og
  // sier samtidig hvilken metode kunden bekreftet e-posten med (lenke vs kode).
  verificationToken: text("verification_token"),
  verificationTokenUsedAt: timestamp("verification_token_used_at"),
  verificationSentAt: timestamp("verification_sent_at"),
  verifiedAt: timestamp("verified_at"),

  // Audit trail
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  
  signedAt: timestamp("signed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertContractSignatureSchema = createInsertSchema(contractSignatures).omit({
  id: true,
  signingId: true,
  signedAt: true,
  createdAt: true,
  verificationSentAt: true,
  verifiedAt: true,
  verificationTokenUsedAt: true,
});

export type InsertContractSignature = z.infer<typeof insertContractSignatureSchema>;
export type ContractSignature = typeof contractSignatures.$inferSelect;

// Frozen signed contract PDF (juridisk audit K4). Ved signering genereres en
// PDF som allerede sendes som e-postvedlegg; her fryses NØYAKTIG den PDF-en
// immutabelt i databasen (dekkes av nattlig pg_dump) som dispute-bevis. Egen
// 1:1-tabell (ikke kolonne på contracts) slik at den store base64-blobben ikke
// dras med i hver SELECT på den varme contracts-raden. PK = contractId gjør
// frysen idempotent (første frys vinner; senere kall er no-op).
export const contractSignedPdfs = pgTable("contract_signed_pdfs", {
  contractId: varchar("contract_id", { length: 36 }).primaryKey(),
  pdfBase64: text("pdf_base64").notNull(), // hele PDF-en base64-kodet (text > bytea her: ingen driver-særheter, trivielt pg_dump-bart)
  sha256: text("sha256").notNull(), // SHA-256 over de rå PDF-bytene (integritetssjekk av selve filen)
  byteSize: integer("byte_size").notNull(), // rå PDF-størrelse i bytes
  contentHash: text("content_hash"), // kryssref til contracts.signedContentHash slik den var ved frys
  frozenAt: timestamp("frozen_at").defaultNow(),
});

export type ContractSignedPdf = typeof contractSignedPdfs.$inferSelect;

// Contract form schema
export const createContractFormSchema = z.object({
  quoteId: z.string().min(1, "Tilbud er påkrevd"),
  terms: z.string().min(1, "Kontraktvilkår er påkrevd"),
  washingIncluded: z.boolean().default(false),
  chargingIncluded: z.boolean().default(false),
  expiresAt: z.string().optional(),
});

export type CreateContractForm = z.infer<typeof createContractFormSchema>;

// Signature verification request schema
export const signatureVerificationSchema = z.object({
  contractId: z.string().min(1),
  signerName: z.string().min(1, "Navn er påkrevd"),
  signerEmail: z.string().email("Ugyldig e-postadresse"),
});

export type SignatureVerificationRequest = z.infer<typeof signatureVerificationSchema>;

// Complete signature schema
export const completeSignatureSchema = z.object({
  contractId: z.string().min(1),
  signatureId: z.string().min(1),
  verificationCode: z.string().min(4, "Kode er påkrevd"),
  signatureImage: z.string().min(1, "Signatur er påkrevd"),
});

export type CompleteSignatureRequest = z.infer<typeof completeSignatureSchema>;

// Admin code bypass signature schema (server-configured code bypasses email verification)
export const adminBypassSignatureSchema = z.object({
  contractId: z.string().min(1),
  adminCode: z.string().min(6, "Adminkode er påkrevd"),
  signerName: z.string().min(1, "Navn er påkrevd"),
  signerEmail: z.string().email("Ugyldig e-postadresse"),
  signatureImage: z.string().min(1, "Signatur er påkrevd"),
});

export type AdminBypassSignatureRequest = z.infer<typeof adminBypassSignatureSchema>;

// Admin counter-signature schema (for admin to sign after customer)
export const adminCounterSignatureSchema = z.object({
  contractId: z.string().min(1),
});

export type AdminCounterSignatureRequest = z.infer<typeof adminCounterSignatureSchema>;

// Brønnøysund registry response type
export interface BrregCompanyData {
  organisasjonsnummer: string;
  navn: string;
  organisasjonsform?: {
    kode: string;
    beskrivelse: string;
  };
  forretningsadresse?: {
    adresse?: string[];
    postnummer?: string;
    poststed?: string;
    landkode?: string;
  };
  postadresse?: {
    adresse?: string[];
    postnummer?: string;
    poststed?: string;
  };
  stiftelsesdato?: string;
  hjemmeside?: string;
  antallAnsatte?: number;
}

// Default contract terms template (Norwegian)
export const DEFAULT_CONTRACT_TERMS = `LEIEAVTALE FOR UTSTYR

1. GENERELLE VILKÅR
Denne avtalen regulerer leie av utstyr mellom Norsk Silent Disco AS (Utleier) og Leietaker.

2. LEIEPERIODE
Leieperioden starter ved utsendelse av utstyret og avsluttes når utstyret er returnert til Utleier i henhold til avtalt dato.

3. UTSTYR
Leietaker bekrefter at utstyret er mottatt i god stand og forplikter seg til å returnere det i samme stand.

4. ANSVAR
Leietaker er ansvarlig for utstyret i hele leieperioden. Ved tap, skade eller tyveri er Leietaker erstatningsansvarlig.

5. VASK OG LADING
Leietaker skal returnere utstyret vasket og ladet med mindre annet er avtalt. Dersom utstyret returneres uvasket eller uladet, vil det påløpe et gebyr.

6. BEKREFTELSE AV MOTTAK
Leietaker har 4 timer etter levering til å bekrefte mottak og rapportere eventuelle avvik. Etter denne fristen godkjennes leveransen automatisk.

7. FORSINKELSE
Ved forsinket retur kan det påløpe ekstra leiekostnad per påbegynt dag.

8. TIMESBASERTE TJENESTER
For tjenester som faktureres etter timespris beregnes medgått tid for oppmøte, arbeid og retur. Tid faktureres per påbegynte halvtime, ikke per minutt eller mindre tidsenhet. I tillegg påløper en administrasjonskostnad tilsvarende én påbegynt halvtime per oppdrag.

9. SIGNERING
Ved signering av denne avtalen bekrefter begge parter at de har lest og forstått vilkårene.`;

// Standard clause stating hourly services are billed per started half-hour.
// Appended to contract terms when a quote/contract contains hourly (timespris) services.
export const HALF_HOUR_BILLING_TERM =
  "Timesbaserte tjenester faktureres per påbegynte halvtime (30 min), ikke per minutt eller mindre tidsenhet. Medgått tid omfatter oppmøte, arbeid og retur. I tillegg påløper en administrasjonskostnad tilsvarende én påbegynt halvtime per oppdrag.";

// Archive folders (3-level hierarchy: Main -> Sub -> Level 3)
export const archiveFolders = pgTable("archive_folders", {
  id: varchar("id", { length: 36 }).primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  shortDescription: text("short_description"),
  description: text("description"),
  parentId: varchar("parent_id", { length: 36 }), // null = root folder
  level: integer("level").notNull().default(1), // 1, 2, or 3
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertArchiveFolderSchema = createInsertSchema(archiveFolders).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertArchiveFolder = z.infer<typeof insertArchiveFolderSchema>;
export type ArchiveFolder = typeof archiveFolders.$inferSelect;

// Archive files
export const archiveFiles = pgTable("archive_files", {
  id: varchar("id", { length: 36 }).primaryKey(),
  folderId: varchar("folder_id", { length: 36 }).notNull(),
  displayName: varchar("display_name", { length: 255 }).notNull(),
  originalName: varchar("original_name", { length: 255 }).notNull(),
  shortDescription: text("short_description"),
  description: text("description"),
  mimeType: varchar("mime_type", { length: 100 }),
  size: integer("size"), // bytes
  storageKey: text("storage_key").notNull(), // file path or storage key
  url: text("url"), // public URL if available
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertArchiveFileSchema = createInsertSchema(archiveFiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertArchiveFile = z.infer<typeof insertArchiveFileSchema>;
export type ArchiveFile = typeof archiveFiles.$inferSelect;

// Archive folder with children and files for tree view
export interface ArchiveFolderWithChildren extends ArchiveFolder {
  children: ArchiveFolderWithChildren[];
  files: ArchiveFile[];
}

// Search result types
export interface ArchiveSearchResult {
  type: "folder" | "file";
  id: string;
  name: string;
  description: string | null;
  breadcrumb: string[];
  folderId?: string; // for files
}

// Error severity levels
export type ErrorSeverity = "debug" | "info" | "warning" | "error" | "critical";

// Error domains for error codes
export const ERROR_DOMAINS = {
  AUTH: "AUTH",     // Authentication/authorization errors
  DB: "DB",         // Database errors
  API: "API",       // API route errors
  STORAGE: "STORAGE", // Storage layer errors
  UI: "UI",         // Frontend/UI errors
  ORDER: "ORDER",   // Order-related errors
  QUOTE: "QUOTE",   // Quote-related errors
  INV: "INV",       // Inventory errors
  SHIP: "SHIP",     // Shipping errors
  SYS: "SYS",       // System errors
} as const;

export type ErrorDomain = keyof typeof ERROR_DOMAINS;

// Error log severity labels
export const ERROR_SEVERITY_LABELS: Record<ErrorSeverity, string> = {
  debug: "Debug",
  info: "Info",
  warning: "Advarsel",
  error: "Feil",
  critical: "Kritisk",
};

// Error logs table
export const errorLogs = pgTable("error_logs", {
  id: varchar("id", { length: 36 }).primaryKey(),
  errorCode: varchar("error_code", { length: 20 }).notNull(), // e.g., AUTH-0001, DB-0103
  domain: varchar("domain", { length: 20 }).notNull(), // AUTH, DB, API, etc.
  severity: varchar("severity", { length: 20 }).notNull().default("error"),
  message: text("message").notNull(),
  
  // Reference to what failed
  reference: text("reference"), // e.g., "Order #12345", "Quote Q-2024-001"
  referenceType: varchar("reference_type", { length: 50 }), // order, quote, user, etc.
  referenceId: varchar("reference_id", { length: 36 }), // ID of the referenced entity
  
  // User context
  userId: varchar("user_id", { length: 36 }),
  userRole: varchar("user_role", { length: 50 }),
  username: varchar("username", { length: 255 }),
  
  // Request context (for API errors)
  requestMethod: varchar("request_method", { length: 10 }), // GET, POST, etc.
  requestPath: text("request_path"),
  requestQuery: text("request_query"),
  requestBody: text("request_body"), // JSON string (sanitized, no passwords)
  
  // Client context (for frontend errors)
  clientUrl: text("client_url"),
  clientUserAgent: text("client_user_agent"),
  clientBrowser: varchar("client_browser", { length: 100 }),
  
  // Error details
  stack: text("stack"), // Stack trace
  metadata: text("metadata"), // JSON string with additional context
  
  // Resolution tracking
  resolved: boolean("resolved").default(false),
  resolvedAt: timestamp("resolved_at"),
  resolvedBy: varchar("resolved_by", { length: 36 }),
  resolvedNotes: text("resolved_notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertErrorLogSchema = createInsertSchema(errorLogs).omit({
  id: true,
  createdAt: true,
  resolved: true,
  resolvedAt: true,
  resolvedBy: true,
  resolvedNotes: true,
});

export type InsertErrorLog = z.infer<typeof insertErrorLogSchema>;
export type ErrorLog = typeof errorLogs.$inferSelect;

// Common error codes registry
export const ERROR_CODES = {
  // Authentication errors (AUTH-0001 to AUTH-0099)
  "AUTH-0001": "Ugyldig brukernavn eller passord",
  "AUTH-0002": "Sesjonen har utløpt",
  "AUTH-0003": "Ikke autorisert tilgang",
  "AUTH-0004": "Utilstrekkelige rettigheter",
  "AUTH-0005": "Brukerkonto er deaktivert",
  
  // Database errors (DB-0001 to DB-0099)
  "DB-0001": "Database tilkoblingsfeil",
  "DB-0002": "Feil ved lesing av data",
  "DB-0003": "Feil ved lagring av data",
  "DB-0004": "Feil ved sletting av data",
  "DB-0005": "Data ikke funnet",
  "DB-0006": "Duplisert nøkkel",
  
  // API errors (API-0001 to API-0099)
  "API-0001": "Ugyldig forespørsel",
  "API-0002": "Ressurs ikke funnet",
  "API-0003": "Metode ikke tillatt",
  "API-0004": "Intern serverfeil",
  "API-0005": "Validering feilet",
  
  // Storage errors (STORAGE-0001 to STORAGE-0099)
  "STORAGE-0001": "Fil ikke funnet",
  "STORAGE-0002": "Feil ved opplasting av fil",
  "STORAGE-0003": "Feil ved lesing av fil",
  "STORAGE-0004": "Feil ved sletting av fil",
  
  // Order errors (ORDER-0001 to ORDER-0099)
  "ORDER-0001": "Ordre ikke funnet",
  "ORDER-0002": "Ugyldig ordrestatus",
  "ORDER-0003": "Utilstrekkelig lagerbeholdning",
  "ORDER-0004": "Feil ved pakking",
  "ORDER-0005": "Feil ved sending",
  
  // Quote errors (QUOTE-0001 to QUOTE-0099)
  "QUOTE-0001": "Tilbud ikke funnet",
  "QUOTE-0002": "Ugyldig tilbudsstatus",
  "QUOTE-0003": "Tilbud er utløpt",
  
  // Inventory errors (INV-0001 to INV-0099)
  "INV-0001": "Produkt ikke funnet",
  "INV-0002": "Utilstrekkelig lagerbeholdning",
  "INV-0003": "Feil ved lagertelleføring",
  "INV-0004": "Lager ikke funnet",
  
  // Shipping errors (SHIP-0001 to SHIP-0099)
  "SHIP-0001": "Sendingsmetode ikke støttet",
  "SHIP-0002": "Ugyldig adresse",
  "SHIP-0003": "Feil ved prisberegning",
  
  // System errors (SYS-0001 to SYS-0099)
  "SYS-0001": "Ukjent systemfeil",
  "SYS-0002": "Konfigurasjonsfeil",
  "SYS-0003": "Ekstern tjeneste utilgjengelig",
} as const;

export type ErrorCode = keyof typeof ERROR_CODES;

// Email direction
export type EmailDirection = "inbound" | "outbound";

// Email status
export type EmailStatus = "pending" | "sent" | "delivered" | "failed" | "read" | "unread";

// Emails table for inbox/outbox
export const emails = pgTable("emails", {
  id: varchar("id", { length: 36 }).primaryKey(),
  direction: varchar("direction", { length: 20 }).notNull(), // inbound or outbound
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  
  // Email addresses
  fromEmail: text("from_email").notNull(),
  fromName: text("from_name"),
  toEmail: text("to_email").notNull(),
  toName: text("to_name"),
  replyTo: text("reply_to"),
  
  // Content
  subject: text("subject").notNull(),
  bodyText: text("body_text"),
  bodyHtml: text("body_html"),
  
  // References
  orderId: varchar("order_id", { length: 36 }),
  quoteId: varchar("quote_id", { length: 36 }),
  customerId: varchar("customer_id", { length: 36 }),
  
  // Email metadata
  messageId: text("message_id"), // For threading
  inReplyTo: text("in_reply_to"), // Reference to parent email
  threadId: varchar("thread_id", { length: 36 }), // Group related emails
  
  // Email type for categorization
  emailType: varchar("email_type", { length: 50 }), // contract_code, order_shipped, confirmation, reply, manual
  
  // Timestamps
  sentAt: timestamp("sent_at"),
  receivedAt: timestamp("received_at"),
  readAt: timestamp("read_at"),
  createdAt: timestamp("created_at").defaultNow(),
  
  // Error tracking
  errorMessage: text("error_message"),
  retryCount: integer("retry_count").default(0),
});

export const insertEmailSchema = createInsertSchema(emails).omit({
  id: true,
  createdAt: true,
});

export type InsertEmail = z.infer<typeof insertEmailSchema>;
export type Email = typeof emails.$inferSelect;

// Company settings table (singleton - only one row)
export const companySettings = pgTable("company_settings", {
  id: varchar("id", { length: 36 }).primaryKey(),
  
  // Company information for contracts and invoices
  companyName: text("company_name").notNull(),
  companyLogo: text("company_logo"), // Base64 encoded company logo image
  companyAddress: text("company_address"),
  companyAddress2: text("company_address_2"), // Second address line
  companyPostalCode: varchar("company_postal_code", { length: 10 }),
  companyCity: text("company_city"),
  companyPhone: text("company_phone"),
  companyEmail: text("company_email"),
  companyOrgNumber: varchar("company_org_number", { length: 20 }),
  companyWebsite: text("company_website"),

  // Payment / betalingsinformasjon shown on quotes and contracts
  bankAccountNumber: text("bank_account_number"), // Kontonummer
  iban: text("iban"), // IBAN (utland)
  bicSwift: varchar("bic_swift", { length: 20 }), // BIC/SWIFT (utland)
  vippsNumber: text("vipps_number"), // Vipps-nummer
  paymentInfo: text("payment_info"), // Fritekst betalingsinformasjon
  defaultPaymentTerms: text("default_payment_terms"), // Standard betalingsbetingelser (f.eks. "14 dager")

  // System domain setting
  systemDomain: text("system_domain").notNull().default("todosystem.no"),
  
  // Email settings (optional overrides)
  emailFromName: text("email_from_name"),
  emailFromAddress: text("email_from_address"),

  // Notification settings. signingNotifyEmails: comma-separated recipients
  // notified internally when a contract is signed (default fredrik+oyvind).
  // alertEmail: where error-log alerts go (overrides ALERT_EMAIL env).
  signingNotifyEmails: text("signing_notify_emails"),
  alertEmail: text("alert_email"),

  // Fiken-integrasjon: OAuth refresh-token er roterbart og persisteres her
  // (leses foerste gang fra env FIKEN_REFRESH_TOKEN, deretter fra DB).
  fikenRefreshToken: text("fiken_refresh_token"),

  // Vipps-API-noekler for "Lag Vipps-lenke automatisk"-knappen paa ordrer.
  // Fallback naar VIPPS_CLIENT_ID/SECRET/SUBSCRIPTION_KEY/MSN ikke er satt i
  // env (ingen servertilgang til aa sette dem) -- env vinner felt-for-felt
  // over disse, se getVippsConfig i server/vipps.ts.
  vippsClientId: text("vipps_client_id"),
  vippsClientSecret: text("vipps_client_secret"),
  vippsSubscriptionKey: text("vipps_subscription_key"),
  vippsMsn: text("vipps_msn"),

  // SMTP mail server settings
  smtpHost: text("smtp_host"),
  smtpPort: integer("smtp_port"),
  smtpUser: text("smtp_user"),
  smtpPassword: text("smtp_password"),
  smtpSecure: boolean("smtp_secure").default(false),
  smtpVerified: boolean("smtp_verified").default(false),
  smtpLastVerifiedAt: timestamp("smtp_last_verified_at"),
  
  // Admin signature settings
  adminSignatureImage: text("admin_signature_image"), // Base64 encoded admin signature (text for large images)
  adminSignerName: text("admin_signer_name"), // Full name for admin signatures
  adminSignerEmail: text("admin_signer_email"), // Email for admin signatures

  // Avvik og gebyr: satsene for tapte headset, redigerbare i Innstillinger.
  // Standardvilkaarene (alle signerte kontrakter siden 20.5.2026): "Tapte
  // headset erstattes med kr. 650,- pr. stk. ved tap av inntil 15% av totalt
  // antall leide headset". Derfor default 650 og 15 -- endres satsen i
  // vilkaarene, endres den her, ikke i koden (server/discrepancy-fees.ts).
  missingHeadsetReplacementNok: integer("missing_headset_replacement_nok").default(650),
  missingHeadsetFlatRateSharePercent: integer("missing_headset_flat_rate_share_percent").default(15),

  // Tilbud til nye henvendelser (server/inquiry-offer-text.ts): prisene og de
  // faste avsnittene i tilbudsteksten som sendes fra prishenvendelser, gjort
  // redigerbare her. Feltene er NULLBARE UTEN default fordi null har en egen
  // betydning: "ikke satt, bruk standardverdien/-teksten i koden" (se
  // offerOverridesFromSettings). Det skiller "ikke satt" fra en bevisst 0
  // (f.eks. gratis lading), og gjoer at kodens standardverdier kan endres
  // senere uten aa rulle ut en DB-migrering paa nytt.
  offerPriceHeadsetPremium: integer("offer_price_headset_premium"),
  offerPriceHeadsetPlus: integer("offer_price_headset_plus"),
  offerPriceHeadsetDeluxe: integer("offer_price_headset_deluxe"),
  offerPriceCharging: integer("offer_price_charging"),
  offerPriceSender: integer("offer_price_sender"),
  offerSenderCount: integer("offer_sender_count"),
  offerFreightPerKolli: integer("offer_freight_per_kolli"),
  offerMinPrice: integer("offer_min_price"),
  offerIntroText: text("offer_intro_text"),
  offerPickupText: text("offer_pickup_text"),
  offerFreightText: text("offer_freight_text"),

  // Timestamps
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: varchar("updated_by", { length: 36 }),
});

export const insertCompanySettingsSchema = createInsertSchema(companySettings).omit({
  id: true,
  updatedAt: true,
});

export const updateCompanySettingsSchema = insertCompanySettingsSchema.partial();

export type InsertCompanySettings = z.infer<typeof insertCompanySettingsSchema>;
export type UpdateCompanySettings = z.infer<typeof updateCompanySettingsSchema>;
export type CompanySettings = typeof companySettings.$inferSelect;

// Standard packages (pre-configured combinations of products and services)
export const standardPackages = pgTable("standard_packages", {
  id: varchar("id", { length: 36 }).primaryKey(),
  name: text("name").notNull(), // Display name shown in order form
  description: text("description"),
  isActive: boolean("is_active").default(true),
  sortOrder: integer("sort_order").default(0),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertStandardPackageSchema = createInsertSchema(standardPackages).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertStandardPackage = z.infer<typeof insertStandardPackageSchema>;
export type StandardPackage = typeof standardPackages.$inferSelect;

// Items in standard packages (products, services, and shipping)
export const standardPackageItems = pgTable("standard_package_items", {
  id: varchar("id", { length: 36 }).primaryKey(),
  packageId: varchar("package_id", { length: 36 }).notNull(),
  itemType: text("item_type").notNull().default("product"), // "product", "service", or "shipping"
  productId: varchar("product_id", { length: 36 }), // References products table
  serviceId: varchar("service_id", { length: 36 }), // References services table
  shippingOptionId: varchar("shipping_option_id", { length: 36 }), // References shipping_options table
  productName: text("product_name").notNull(), // Cached name for display
  quantity: integer("quantity").notNull().default(1),
  quantityLinkType: varchar("quantity_link_type", { length: 20 }).default("manual"), // "manual", "headset", "kolli"
  sortOrder: integer("sort_order").default(0),
});

export const insertStandardPackageItemSchema = createInsertSchema(standardPackageItems).omit({
  id: true,
});

export type InsertStandardPackageItem = z.infer<typeof insertStandardPackageItemSchema>;
export type StandardPackageItem = typeof standardPackageItems.$inferSelect;

// Preparation task types
export type PreparationTaskType = "washing" | "charging";
export type PreparationTaskStatus = "pending" | "in_progress" | "completed";

// Preparation tasks for washing and charging headsets
export const preparationTasks = pgTable("preparation_tasks", {
  id: varchar("id", { length: 36 }).primaryKey(),
  taskType: text("task_type").notNull(), // "washing" or "charging"
  status: text("status").notNull().default("pending"), // "pending", "in_progress", "completed"
  
  // Source information
  orderId: varchar("order_id", { length: 36 }), // Related order (if from order)
  sourceType: text("source_type").notNull(), // "order_service" (ordered washing), "receiving_rejected" (not approved washed), "manual"
  
  // Quantities
  totalQuantity: integer("total_quantity").notNull(), // Total headsets to process
  completedQuantity: integer("completed_quantity").default(0), // How many have been processed
  
  // Tracking
  createdBy: varchar("created_by", { length: 36 }),
  createdAt: timestamp("created_at").defaultNow(),
  completedAt: timestamp("completed_at"),
  completedBy: varchar("completed_by", { length: 36 }),
  
  // Notes
  notes: text("notes"),
});

export const insertPreparationTaskSchema = createInsertSchema(preparationTasks).omit({
  id: true,
  createdAt: true,
});

export type InsertPreparationTask = z.infer<typeof insertPreparationTaskSchema>;
export type PreparationTask = typeof preparationTasks.$inferSelect;

// Individual preparation work entries (who did what when)
export const preparationWorkEntries = pgTable("preparation_work_entries", {
  id: varchar("id", { length: 36 }).primaryKey(),
  taskId: varchar("task_id", { length: 36 }).notNull(),
  userId: varchar("user_id", { length: 36 }).notNull(),
  quantity: integer("quantity").notNull(), // How many processed in this entry
  createdAt: timestamp("created_at").defaultNow(),
  notes: text("notes"),
});

export const insertPreparationWorkEntrySchema = createInsertSchema(preparationWorkEntries).omit({
  id: true,
  createdAt: true,
});

export type InsertPreparationWorkEntry = z.infer<typeof insertPreparationWorkEntrySchema>;
export type PreparationWorkEntry = typeof preparationWorkEntries.$inferSelect;

// Charging queue - headsets currently on charging stations
export const chargingQueue = pgTable("charging_queue", {
  id: varchar("id", { length: 36 }).primaryKey(),
  taskId: varchar("task_id", { length: 36 }), // Related preparation task
  quantity: integer("quantity").notNull(), // Number of headsets on charging
  status: text("status").notNull().default("pending"), // "pending" (waiting), "charging" (on charger), "ready" (done)
  
  // Tracking
  startedBy: varchar("started_by", { length: 36 }),
  startedAt: timestamp("started_at").defaultNow(),
  completedBy: varchar("completed_by", { length: 36 }),
  completedAt: timestamp("completed_at"),
  
  notes: text("notes"),
});

export const insertChargingQueueSchema = createInsertSchema(chargingQueue).omit({
  id: true,
  startedAt: true,
});

export type InsertChargingQueue = z.infer<typeof insertChargingQueueSchema>;
export type ChargingQueue = typeof chargingQueue.$inferSelect;

// Avspillingsenheter som ENKELTENHETER (g5, g6, ...) -- registeret bak
// Spotify-abonnement-oversikten og aux/USB-C-merkingen i pakkeflyten.
// Navnet er det som er tusjet paa baksiden av enheten. Abonnement lagres som
// det som ble SATT (subscription + premiumUntil for proeveperioder); om
// enheten "maa faa Spotify Premium" UTLEDES av dato i shared/playback-units.ts
// slik at utloepte proeveperioder faller tilbake automatisk uten noen jobb
// som maa flippe status.
export const playbackDeviceUnits = pgTable("playback_device_units", {
  id: varchar("id", { length: 36 }).primaryKey(),
  name: varchar("name", { length: 50 }).notNull(), // tusjet paa baksiden, f.eks. "g5"
  // "trial" = gratis Premium-periode (premiumUntil MAA vaere satt),
  // "family" = paa family-abonnementet, "none" = har ikke Premium.
  subscription: varchar("subscription", { length: 20 }).notNull().default("none"),
  premiumUntil: timestamp("premium_until"), // siste dag proeveperioden gjelder
  // Kortet proeveperioden ble tegnet med (f.eks. "SAS Mastercard", "Curve").
  // Spotify krever et UBRUKT kort per gratisperiode, saa dette feltet er
  // fasiten paa hvilke kort som allerede er brukt opp.
  paymentCard: varchar("payment_card", { length: 100 }),
  hasAux: boolean("has_aux").notNull().default(false), // false -> USB-C-overgang maa legges ved
  isActive: boolean("is_active").notNull().default(true),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertPlaybackDeviceUnitSchema = createInsertSchema(playbackDeviceUnits, {
  premiumUntil: z.coerce.date().nullable().optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPlaybackDeviceUnit = z.infer<typeof insertPlaybackDeviceUnitSchema>;
export type PlaybackDeviceUnit = typeof playbackDeviceUnits.$inferSelect;

// Utlaan av utstyr til samarbeidspartnere (teatre, musikere o.l.): hentes paa
// lageret uten leiepris, kontrakt eller faktura. Egen modul, ikke en ordre:
// ordremaskinen (signaturgate, fakturakladd, betalingsinfo, pakkeliste,
// fraktkoe) skal ikke laere seg unntak for laan. Aktive laan trekkes fra
// tilgjengelig antall i getProductAvailability. Se shared/loans.ts (rene
// hjelpere) og server/loan-overdue.ts (varsel over avtalt retur).
export const loans = pgTable("loans", {
  id: varchar("id", { length: 36 }).primaryKey(),
  loanNumber: text("loan_number").notNull(), // UTL-1, UTL-2, ...
  borrowerName: text("borrower_name").notNull(),
  borrowerOrg: text("borrower_org"),
  borrowerEmail: text("borrower_email"),
  borrowerPhone: text("borrower_phone"),
  warehouseId: varchar("warehouse_id", { length: 36 }).notNull(),
  pickedUpAt: date("picked_up_at").notNull(),
  agreedReturnAt: date("agreed_return_at").notNull(),
  returnedAt: timestamp("returned_at"),
  status: text("status").notNull().default("active"), // active | returned
  notes: text("notes"),
  returnNotes: text("return_notes"),
  overdueNotifiedAt: timestamp("overdue_notified_at"),
  createdById: varchar("created_by_id", { length: 36 }),
  createdByName: text("created_by_name"),
  returnedByName: text("returned_by_name"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertLoanSchema = createInsertSchema(loans).omit({
  id: true,
  loanNumber: true,
  status: true,
  returnedAt: true,
  overdueNotifiedAt: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertLoan = z.infer<typeof insertLoanSchema>;
export type Loan = typeof loans.$inferSelect;

export const loanItems = pgTable("loan_items", {
  id: varchar("id", { length: 36 }).primaryKey(),
  loanId: varchar("loan_id", { length: 36 }).notNull(),
  productId: varchar("product_id", { length: 36 }),
  productType: text("product_type").notNull(),
  productName: text("product_name").notNull(),
  quantity: integer("quantity").notNull().default(1),
});

export const insertLoanItemSchema = createInsertSchema(loanItems).omit({ id: true, loanId: true });
export type InsertLoanItem = z.infer<typeof insertLoanItemSchema>;
export type LoanItem = typeof loanItems.$inferSelect;
export type LoanWithItems = Loan & { items: LoanItem[] };

const isoDateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Dato må være YYYY-MM-DD");

// POST /api/loans: laanet pluss minst en utstyrslinje.
export const createLoanRequestSchema = insertLoanSchema
  .extend({
    borrowerName: z.string().trim().min(1, "Låntaker må ha et navn"),
    warehouseId: z.string().min(1, "Velg lager"),
    pickedUpAt: isoDateString,
    agreedReturnAt: isoDateString,
    items: z
      .array(
        insertLoanItemSchema.extend({
          productName: z.string().trim().min(1, "Utstyrslinjen mangler navn"),
          productType: z.string().trim().min(1),
          quantity: z.coerce.number().int().min(1, "Antall må være minst 1"),
        }),
      )
      .min(1, "Legg til minst én utstyrslinje"),
  })
  .refine((v) => v.agreedReturnAt >= v.pickedUpAt, {
    message: "Avtalt retur kan ikke være før hentedato",
    path: ["agreedReturnAt"],
  });

// PATCH /api/loans/:id: bare det som kan endres etter henting.
export const updateLoanRequestSchema = z
  .object({
    borrowerName: z.string().trim().min(1).optional(),
    borrowerOrg: z.string().nullable().optional(),
    borrowerEmail: z.string().nullable().optional(),
    borrowerPhone: z.string().nullable().optional(),
    agreedReturnAt: isoDateString.optional(),
    notes: z.string().nullable().optional(),
  })
  .strict();

// Sessions table for connect-pg-simple
export const sessions = pgTable("session", {
  sid: varchar("sid", { length: 255 }).primaryKey(),
  sess: json("sess").notNull(),
  expire: timestamp("expire", { precision: 6 }).notNull(),
});
